<template>
  <div class="inline-descriptor-wrapper">
    <div
      ref="container"
      class="inline-descriptor"
      :class="{ 'inline-descriptor--request-pending': valueRequestPending }"
      :style="isTable ? 'display: contents;' : 'display: inline;'"
      :id="'contid' + id"
    ></div>
    <teleport :disabled="!moveToBody" to="body">
      <div
        ref="contextMenu"
        class="fmenu"
        v-if="enableMenu && showMenu"
        :style="`left: ${menuLeft}; top: ${menuTop}; right: ${menuRight}; bottom: ${menuBottom}; width: min(max-content, ${maxWidth}); max-width: ${maxWidth}; height: ${maxHeight}; max-height: ${maxHeight}; overflow: hidden;${menuZIndex ? ' z-index: ' + menuZIndex + ';' : ''}`"
        tabindex="-1"
        @blur="($event) => onBlur($event, true)"
        @keydown="onFmenuKeydown"
      >
        <div class="fmenu__scroll">
          <slot name="menu-content"></slot>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script lang="ts">
/*
[Инициализация компонента]
       |
       v
[Mounted: Создание DOM и привязка событий]
       |
       v
[BuildContent: Построение секций и токенов из modelValue]
       |
       v
[Watch modelValue: Обновление DOM при изменении модели]
       |                                   |
       v                                   v
[Watch activeKey: Активация редактирования секции]  [Click: Запуск редактирования секции]
       |                                   
       v                                   
[OnClick: Выбор секции, установка фокуса] 
       |                                   
       v                                   
[OnInput: Обработка ввода текста]
       |                                   
       v                                   
[NormalizeSection: Разделение текста на токены]  [UpdateMenu: Показ меню с опциями]
       |                                   |
       v                                   v
[SetCursorCharPos: Установка позиции курсора]  [SelectOption: Вставка выбранной опции]
       |
       v
[OnKeydown: Обработка Backspace/Delete/Enter]
       |                                   |
       v                                   v
[HandleBackspaceAtStart/DeleteAtEnd: Навигация]  [OnBlur: Сохранение и завершение]
       |                                   
       v                                   
[SaveModel: Обновление modelValue] 
       |
       v
[UpdateSection: Обновление конкретной секции]

**Описание сценария:**

1. **Инициализация:** 
Компонент монтируется (`mounted`), создаётся DOM-структура (`buildContent`) на основе `modelValue` и `schema`. 
Привязываются обработчики событий (`click`, `input`, `keydown`, `blur`, `paste`, `compositionstart/end`, `selectionchange`).

2. **Обновление модели:** 
При изменении `modelValue` (watch) вызывается `buildContent`, если нет активной секции.

3. **Активация редактирования:** 
При изменении `activeKey` (watch) устанавливается фокус на секцию, курсор позиционируется в последнем токене 
(`setCursorCharPos`), и начинается отслеживание выбора (`onSelectionChange`).

4. **Клик по секции:** 
`onClick` определяет секцию, эмитит `request-edit`, если секция не активна, и создаёт пустой токен для пустой секции.

5. **Ввод текста:** 
`onInput` нормализует текст (`normalizeSection`), разделяя его на `span` (слова и пробелы), и обновляет позицию курсора. 
Если текст в `container`, переносится в активную секцию.

6. **Меню опций:** 
`updateMenu` показывает меню для типов `select`/`select+free`, фильтруя опции по вводу (`getOptionTokens`). 
Выбор опции (`selectOption`) вставляет её в текущий токен.

7. **Навигация:** 
`onKeydown` обрабатывает Enter (сохранение), Backspace/Delete (переход между секциями через 
`handleBackspaceAtStart`/`handleDeleteAtEnd`).

8. **Переход между секциями:** 
`onSelectionChange` отслеживает смену секции, сохраняет текущую (`saveModel`), эмитит `done-edit` и `request-edit`.

9. **Сохранение: 
`onBlur` или Enter вызывают `saveModel`, обновляют `modelValue` и эмитят `done-edit`. 
Секция обновляется через `updateSection`.
*/

import { Component, Prop, Emit, Ref, Watch, Vue } from "vue-facing-decorator";
import { useDebounceFn } from "@vueuse/core";
import { h, reactive, render, Component as vue_c } from "vue";
import { normalizeTokens } from "@/utils/auxfunc";
import { findRecursive } from "../../../models/helpers/recursive";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import _ from "lodash";

/**
 * Находит ближайшего предка, который соответствует селектору И условию.
 *
 * @param {Element} startElement Элемент, от которого начинается поиск (включая его).
 * @param {string} selector Селектор CSS для поиска предка (например, '.section').
 * @param {function(Element): boolean} condition Функция, которая принимает элемент и возвращает true, если он подходит.
 * @returns {Element|null} Ближайший элемент, соответствующий обоим критериям, или null, если не найден.
 */
function findClosestWithConditionOuter(
  startElement: HTMLElement,
  selector: string,
  condition: any
) {
  let currentElement = startElement;

  // Перемещаемся вверх по DOM-дереву
  while (currentElement) {
    // console.log('check')
    // console.log(currentElement)
    // console.log(currentElement.matches(selector))
    // console.log(condition(currentElement))
    // Проверяем, соответствует ли текущий элемент селектору И условию
    if (currentElement.matches(selector) && condition(currentElement)) {
      // console.log('found')
      // console.log(currentElement)
      return currentElement; // Найдено совпадение
    }

    if (currentElement.parentElement) {
      // Поднимаемся к родительскому элементу
      currentElement = currentElement.parentElement;
    } else {
      return null;
    }
  }

  return null; // Совпадение не найдено до достижения корня DOM
}

type OptionSrc = any[];
export interface Token {
  text: string;
  color?: string;
  type: string;
  id?: string | string[];
  selectorType?: string;
  selectorData?: any;
  clickable: boolean;
  range?: { min: number; max: number };
}

export interface SchemaField {
  key: string;
  // Ключ единицы разметки для сверки. Заполняет его тот, кто строит схему, потому что
  // только он знает устойчивый признак. Ключ поля на эту роль не годится, он позиционный,
  // в нём лежит индекс элемента, и при удалении элемента ключи соседей переезжают.
  // Если не заполнен, берётся ключ поля
  unitKey?: string;
  title: string;
  type: string;
  options?: OptionSrc;
  separator?: string | null;
  inputPattern?: RegExp;
  prefix?: string;
  allowDelete?: boolean;
  axis?: string;
  placeholder?: string;
  nestedSchema?: SchemaField[]; // Для вложенных секций
  component?: vue_c;
  isList?: boolean;
  readonly?: boolean; // только просмотр (например Formula) - секция становится нередактируемой
}

// Единица разметки одного поля схемы. Узлов может быть несколько, рядом с секцией идут
// префикс и ячейки таблицы, а вертикальный уровень отдаёт сразу несколько строк
interface UnitRecord {
  key: string;
  shape: string;
  nodes: Node[];
  section: HTMLElement | null;
  hosts: HTMLElement[];
  childKeys: string[];
}

// Переключатель на прежний полный проход. Нужен, чтобы сравнить поведение один в один
// и быстро откатиться, если на большом параметре вылезет неожиданное
const KEYED_STRUCTURE_PASS = true;

@Component({
  name: "InlineDescriptor",
  emits: [
    "update:modelValue",
    "chip-added",
    "chip-removed",
    "chip-selected",
    "update-menu",
    "add-list-item",
    "remove-list-item",
    "clear-field",
    "add-value",
    "title-action",
    "reportWidths",
  ],
  expose: [
    "openMenu",
    "hideMenu",
    "activateSectionByKey",
    "releaseActiveField",
    "activeFieldRect",
    "closeValueSetFieldAsOutside",
    "applyModel",
    "clearListActionState",
  ],
  components: { InlineDescriptor },
})
export default class InlineDescriptor extends Vue {
  @Prop({ type: String, required: true }) id!: string;
  @Prop({ type: Object, required: true }) modelValue!: Record<
    string,
    Token[] | Record<string, Token[]> | Token[][]
  >;
  // Идёт запрос по значению поля. Признак ставит корневой параметр, а значки ищут его
  // подъёмом по предкам: у записи они живут во вложенном описателе.
  @Prop({ type: Boolean, default: false }) valueRequestPending!: boolean;

  @Prop({ type: Boolean, default: false }) enableMenu!: boolean;
  @Prop({ type: Boolean, default: false }) moveToBody!: boolean;
  @Prop({ type: Boolean, default: false }) isVertical!: boolean;

  // Ключ поля, которому сейчас доступно добавление значения в набор. Условия этого
  // состояния целиком принадлежат параметрам, поэтому считает их родитель, а здесь
  // только адрес обёртки, на которой показать значок.
  @Prop({ default: undefined }) addValueFieldKey?: string;

  // Верхний уровень параметра. Заголовок списка на нём крупнее, чем на вложенных.
  @Prop({ type: Boolean, default: false }) isRootLevel!: boolean;

  // Плавающая метка в поле. Выключается там, где имя параметра уже показано заголовком
  // рядом с полем: у одиночного значения метка повторяла бы этот заголовок.
  @Prop({ type: Boolean, default: true }) showFieldLabel!: boolean;

  @Prop({ type: HTMLElement })
  contextTrigger?: HTMLElement = undefined;

  @Prop({ type: Boolean, default: false }) isTable?: boolean;
  @Prop({ type: Boolean, default: true }) createMode?: boolean;
  @Prop({ type: Array<SchemaField>, required: true }) schema!: SchemaField[];
  @Prop({ type: Object }) tokenStyles?: Record<string, string>;
  @Prop({ type: Boolean, default: false }) boldSelected?: boolean;
  @Prop({ type: String }) placeholder?: string;
  @Prop({ type: String, default: "down" }) menuPreferredDirection!:
    | "down"
    | "up";
  @Prop({
    type: Function,
    default: (token: Token, field: SchemaField) => token,
  })
  updateToken!: (token: Token, field: SchemaField) => Token;
  @Prop({ type: Array, default: () => [","] }) separators!: string[];

  @Prop({ type: Boolean, default: false }) editable!: boolean;
  /**
   * Режим, в котором меню открывается и для простых полей, а не только для ValueSet.
   * Значение считает корневой ParameterInline и передаёт вниз по всей вложенности,
   * потому что решение об открытии меню принимает только он.
   */
  @Prop({ type: Boolean, default: false }) plainFieldsOpenMenu!: boolean;
  @Prop({ type: Number, default: 0 }) menuZIndex!: number;

  @Ref("container") readonly container!: HTMLDivElement;
  @Ref("contextMenu") readonly contextMenuRef!: any;

  showMenu = false;
  /** Payload для закрытия меню — кэшируется при открытии, чтобы закрытие по клику снаружи работало и после клика по скроллбарю (когда selection уже не в секции). */
  private cachedClosePayload: {
    fieldKey: string | null;
    sectionText: string;
    eventKind: number;
    nestedIndex: string | undefined;
    opts: any;
  } | null = null;
  rafId: number | null = null;
  menuLeft = "unset";
  menuTop = "unset";
  menuRight = "unset";
  menuBottom = "unset";
  numberLeft: number = 0;
  numberTop: number = 0;
  numberRight: number = 0;
  numberBottom: number = 0;

  menuOffsetLeft: number = 0;
  menuOffsetTop: number = 0;

  childRefs: Map<string, any> = new Map();
  childContainers: Map<string, any> = new Map();
  childVNodes: Map<string, any> = new Map();

  // debounced refresh menu event
  throttled: any;
  private nestedRenderers: HTMLElement[] = []; // Для хранения контейнеров вложенных компонентов

  // Куда складываются узлы собираемой единицы. Сборка идёт во фрагмент, чтобы единицу
  // можно было собрать отдельно от контейнера и поставить её на место уже готовой
  private buildTarget: Node | null = null;

  // Разметка, которая сейчас стоит в контейнере, по ключам единиц
  private units: Map<string, UnitRecord> = new Map();

  // Сборщики для единицы, которая собирается прямо сейчас. Нужны, чтобы при удалении
  // снимать только её поддеревья, а не все подряд
  private unitHosts: HTMLElement[] | null = null;
  private unitChildKeys: string[] | null = null;

  // Сборка vnode по актуальным данным. Одна и та же и при монтировании, и при обновлении,
  // поэтому у ребёнка не остаётся ни устаревших пропов, ни устаревших замыканий
  private childFactories: Map<string, () => any> = new Map();

  // Устойчивое пустое значение по ключу. Новый пустой массив на каждый вызов будил бы
  // у ребёнка вотчер на ровном месте
  private emptyModels: Map<string, any[]> = new Map();

  // Последнее применённое содержимое по ключу секции. По нему видно, что писать нечего
  private appliedContent: Map<string, string> = new Map();

  // Следующий ввод (eventKind 0) это стирание элемента списка. Коммит уходит
  // вместе с ним: обратный прогон токенов читает секцию, и она уже пустая.
  private clearCommit = false;

  maxWidth: string = "800px";
  maxHeight: string = "400px";
  private isComposing = false;
  private skipBlur = false;
  private navigating = false;
  private activeKey: string | undefined = undefined;

  private onClick = (event: PointerEvent): void => {};
  private onFocus = (event: FocusEvent): void => {};
  private onInput = (event: Event): void => {};
  private onPaste = (event: ClipboardEvent): void => {};
  private onKeydown = (event: KeyboardEvent): void => {};
  private onBlur = (event: FocusEvent): void => {};
  private onCompositionStart = (): void => {};
  private onCompositionEnd = (): void => {};
  // Закрытие меню по pointerdown вне поля. Реальная реализация ставится в
  // mounted() через self=this, чтобы внутри arrow корректно работал accessor
  // self.container (он определён только на Vue-proxy, не на raw class-instance,
  // через который инициализируются class-field initializers).
  private _docPointerDownForClose = (e: PointerEvent): void => {};

  /**
   * @brief Emits the update:modelValue event with the updated model.
   *
   * Triggers the update:modelValue event to notify parent components of changes
   * in the modelValue prop, passing the new model object. Used when saving changes
   * from a section to ensure reactive updates in the parent. Handles edge cases where
   * modelValue may be partially updated or invalid for certain field types.
   *
   * @param newModel Record<string, any> The updated model object to emit.
   * @return Record<string, any> The newModel object, returned for event chaining.
   */
  @Emit("update:modelValue")
  emitUpdateModelValue(newModel: Record<string, any>): Record<string, any> {
    return newModel;
  }

  onMenuShow() {
    const that = this;
    this.$nextTick(() => {
      const menu = (that.contextMenuRef as any)?.$el as HTMLElement;
      if (!menu || !that.contextTrigger) return;

      const t = that.contextTrigger.getBoundingClientRect();
      const m = menu.getBoundingClientRect();

      if (window.innerHeight - t.bottom < m.height + 30) {
        menu.style.top = `${t.top - m.height - 8}px`;
        menu.style.transformOrigin = "bottom left";
      } else {
        menu.style.top = `${t.bottom + 8}px`;
        menu.style.transformOrigin = "top left";
      }
      menu.style.left = `${t.left}px`;
    });
  }

  /**
   * @brief Initializes the component and sets up event listeners.
   *
   * Called when the component is mounted. Builds the initial DOM structure using buildContent
   * and attaches event listeners (click, input, keydown, blur, paste, compositionstart/end,
   * selectionchange) to the container. Uses a closure (self) to preserve context in arrow functions.
   * Handles edge cases where container is not yet available or schema is empty.
   *
   * @return void
   */
  mounted(): void {
    const self = this;

    this.throttled = useDebounceFn(self.updateMenu, 45);

    /**
     * @brief Handles click events to activate section editing.
     *
     * Processes clicks on the container, identifies the clicked section, sets clickedSpan, creates an
     * empty token for empty sections, and emits request-edit if the section is not active. Prevents
     * default behavior for non-active sections to avoid focus issues.
     *
     * @param event PointerEvent The pointer event object.
     * @return void
     */
    this.onClick = (event: PointerEvent): void => {
      event.stopImmediatePropagation();
      const target = event.target as HTMLElement;
      const prefix = target.classList.contains("prefix") ? target : null;
      let section: HTMLElement | null = null;

      // parseInt() for making sure the result can be correctly compared
      const addHost = target.closest(
        "[data-add], [data-add_detail]"
      ) as HTMLButtonElement | null;
      if (addHost?.dataset.add_detail) {
        const [templateId, instanceId] = addHost.dataset.add_detail.split("_");
        self.$emit("add-list-item", {
          templateId: parseInt(templateId),
          instanceId: parseInt(instanceId),
        });

        addHost.disabled = true;
        self.appendListLoader(addHost);

        return;
      } else if (addHost?.dataset.add) {
        const [_, templateId] = addHost.dataset.add.split("_");
        self.$emit("add-list-item", {
          templateId: parseInt(templateId),
          instanceId: undefined,
        });

        addHost.disabled = true;
        self.appendListLoader(addHost);

        return;
      }

      if (target.dataset.remove_detail) {
        const [templateId, instanceId, index] =
          target.dataset.remove_detail.split("_");
        self.$emit("remove-list-item", {
          templateId: parseInt(templateId),
          instanceId: parseInt(instanceId),
          index: parseInt(index),
        });
        (target as HTMLButtonElement).disabled = true;
        return;
      } else if (target.dataset.remove) {
        const [_, templateId, index] = target.dataset.remove.split("_");
        self.$emit("remove-list-item", {
          templateId: parseInt(templateId),
          instanceId: undefined,
          index: parseInt(index),
        });
        (target as HTMLButtonElement).disabled = true;
        return;
      }

      if (prefix) {
        let next = target.nextElementSibling;
        while (next && !next.classList.contains("section")) {
          next = next.nextElementSibling;
        }
        section = next as HTMLElement | null;
      } else {
        // section = target.closest('.section') as HTMLElement | null;
        section = findClosestWithConditionOuter(
          target,
          ".section",
          (element: any) => element.dataset.forbid_edit != "1"
        );
      }
      if (!section || !section.dataset.key) return;
      const key = section.dataset.key;

      // TODO: separate this into a common function
      let field: SchemaField | undefined = findRecursive(
        function a(element: any, el_to_find: any): any | undefined {
          if (!element) return undefined;
          if (el_to_find === element.key) {
            return element;
          }
          if (element.nestedSchema) {
            return findRecursive(a, el_to_find, element.nestedSchema);
          }
          return undefined;
        },
        key,
        self.schema
      );
      // END TODO

      if (!field) return;
      if (target.classList.contains("delete")) {
        if (field.allowDelete) {
          self.$emit("chip-removed", key);
          return;
        }
      }

      if (
        field.type === "chip" ||
        (prefix && section.dataset.key !== "input")
      ) {
        if (Array.isArray(self.modelValue[key])) {
          const tokens = (self.modelValue[key] as Token[]) || [];
          const dbId = tokens.length > 0 ? tokens[0].id : undefined;
          self.$emit("chip-selected", { key, dbId });
        }
        return;
      }
      if (
        self.activeKey !== key ||
        (prefix && section.dataset.key !== "input")
      ) {
        const isTouchPointer =
          event.pointerType === "touch" || event.pointerType === "pen";
        if (!isTouchPointer) {
          event.preventDefault();
        }
        const oldKey = self.activeKey;
        if (oldKey) self.saveModel(oldKey);
        self.activeKey = key;

        let activationSpan: HTMLElement | null = null;

        // На мобильных contenteditable должен получить фокус синхронно внутри
        // пользовательского касания, иначе iOS/Chrome не открывают клавиатуру.
        section.contentEditable =
          section.dataset.forbid_edit !== "1" && field.type !== "chip"
            ? "true"
            : "false";
        if (isTouchPointer && section.contentEditable === "true") {
          activationSpan = self.createEmptyToken();
          if (!section.innerHTML) {
            activationSpan.dataset.title = section.dataset.title || "";
            activationSpan.dataset.separator = field.separator || self.separators[0] || ",";
          }
          section.appendChild(activationSpan);
          section.focus({ preventScroll: true });
          self.setCursorCharPos(activationSpan, 0);
        }

        self.$nextTick(() => {
          const activeSection = self.getSectionByKey(key);
          if (activeSection) {
            // activeSection.innerHTML = '';
            const sectionText = activeSection.innerHTML;

            const emptySpan = activationSpan?.isConnected
              ? activationSpan
              : self.createEmptyToken();
            if (!emptySpan.parentElement) {
              activeSection.appendChild(emptySpan);
            }
            activeSection.contentEditable =
              activeSection.dataset.forbid_edit !== "1" &&
              field!.type !== "chip"
                ? "true"
                : "false";

            if (!this.isTable && !sectionText) {
              // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
              // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
              const title = activeSection.dataset.title || "";
              emptySpan.dataset.title = title;
              emptySpan.dataset.separator = this.separators[0] || ',';
            }

            // TODO: make this a separate function for all schema.findIndex()
            const schemaIndex = self.schema.findIndex((f) => f.key === key);
            // Поиск во вложенных nestedSchema, если не найден в корневом schema
            let thisField = self.schema[schemaIndex];
            if (schemaIndex === -1) {
              for (const parentField of self.schema) {
                if (parentField.nestedSchema) {
                  const nestedIndex = parentField.nestedSchema.findIndex(
                    (f) => f.key === key
                  );
                  if (nestedIndex !== -1) {
                    thisField = parentField.nestedSchema[nestedIndex];
                    break;
                  }
                }
              }
            }
            if (thisField && thisField.separator !== null) {
              emptySpan.dataset.separator =
                thisField.separator || self.separators[schemaIndex] || ",";
            }
            self.$nextTick(() => {
              self.setCursorCharPos(emptySpan, 0);
              self.updateMenu(2);
            });
          }
        });
      } else if (!self.showMenu) {
        self.updateMenu(2);
      }
    };

    /**
     * @brief Handles focus events to activate section editing.
     *
     * Processes focus of the container sections, identifies the focused section, creates an
     * empty token for empty sections, and emits request-edit if the section is not active. Prevents
     * default behavior for non-active sections to avoid focus issues.
     *
     * @param event MouseEvent The focus event object.
     * @return void
     */
    this.onFocus = (event: FocusEvent): void => {
      const section = event.target as HTMLElement; // Секция, которая получила фокус
      if (!section || !section.dataset.key) return;

      if (this.activeKey !== section.dataset.key) {
        this.onInput(new Event("input"));
      }
    };

    /**
     * @brief Handles text input events to normalize sections and update cursor.
     *
     * Processes input events in the active section, transfers stray text from container to the active
     * section, normalizes the section (splitting into tokens), repositions the cursor, and updates the
     * options menu. Ignores composition events to avoid IME conflicts. Handles cases where text is input
     * outside the section (e.g., in container).
     *
     * @param event Event The input event object.
     * @return void
     */
    this.onInput = (event: Event): void => {
      if (self.isComposing) return;
      const section = self.getCurrentSection();

      if (!section || !section.dataset.key) {
        // console.log('onInput: section not found or no dataset.key');
        return;
      }

      // if (self.activeKey == section.dataset.key) return;
      self.activeKey = section.dataset.key;

      // Разметку секции сейчас правит сам ввод, и прежний слепок содержимого ей больше
      // не соответствует. Без сброса приход модели с прежним значением был бы принят
      // за уже применённый, и набранное осталось бы висеть поверх модели
      self.appliedContent.delete(section.dataset.key);

      // TODO: separate this into a common function
      let field: SchemaField | undefined = findRecursive(
        function a(element: any, el_to_find: any): any | undefined {
          if (!element) return undefined;
          if (el_to_find === element.key) {
            return element;
          }
          if (element.nestedSchema) {
            return findRecursive(a, el_to_find, element.nestedSchema);
          }
          return undefined;
        },
        section.dataset.key,
        self.schema
      );
      // END TODO

      if (!field) {
        // console.log('onInput: field not found for key', section.dataset.key);
        return;
      }

      const charPos = self.getCursorCharPos(section);
      self.$nextTick(() => {
        // Поле могли отпустить между планированием и этим тиком.
        if (self.activeKey !== section.dataset.key) return;
        self.normalizeSection(section);
        self.setCursorCharPos(section, charPos);
        if (field!.type !== "chip") {
          const tokens = Array.from(
            section.querySelectorAll(".token, .space")
          ).map((span) => ({
            text: span.className === "space" ? " " : span.textContent || "",
            type: span.className === "space" ? "space" : field!.type,
            clickable: field!.type === "chip",
          }));
          tokens.forEach((token) => self.updateToken(token, field!));
        }

        self.throttled();
        section.focus();
        // this.placeCaretAtStart(section);
      });
    };

    /**
     * @brief Handles keydown events for navigation and saving.
     *
     * Processes Enter (save and exit), Backspace (navigate to previous section), and Delete (navigate to
     * next section). Calls handleBackspaceAtStart or handleDeleteAtEnd for navigation and emits events
     * for section transitions. Prevents default behavior for Enter to avoid line breaks.
     *
     * @param event KeyboardEvent The keydown event object.
     * @return void
     */
    this.onKeydown = (event: KeyboardEvent): void => {
      const section = self.getCurrentSection();
      if (!section || section.dataset.key !== self.activeKey) return;
      const key = section.dataset.key;

      // TODO: separate this into a common function
      let field: SchemaField | undefined = findRecursive(
        function a(element: any, el_to_find: any): any | undefined {
          if (!element) return undefined;
          if (el_to_find === element.key) {
            return element;
          }
          if (element.nestedSchema) {
            return findRecursive(a, el_to_find, element.nestedSchema);
          }
          return undefined;
        },
        key,
        self.schema
      );
      // END TODO

      if (!field) return;
      if (event.key === "Enter") {
        event.preventDefault();
        useDebounceFn(() => {
          if (field!.type === "addtext" && section.textContent?.trim()) {
            const text = section.textContent.trim();
            const newKey = `chip_${Date.now()}`;
            self.$emit("chip-added", {
              key: newKey,
              text,
              prefix: field!.prefix,
            });
            // const newModel = { ...self.modelValue, input: [{ text: '', type: 'addtext', clickable: false }], [newKey]: [{ text, type: 'chip', clickable: true, id: newKey }] };
            // self.emitUpdateModelValue(newModel);
            // section.innerHTML = '';
          } else {
            if (self.activeKey) {
              self.saveModel(self.activeKey);
            }
            // Enter доводим до той же ветки, что и blur (eventKind=1). Select не затрагиваем,
            // там Enter обрабатывает ParameterInline выбором значения из селектора.
            // Вызываем до blur(), пока getCurrentSection() доступна.
            if (field!.type !== "select") {
              self.updateMenu(1);
              // В таблице поле после Enter отпускается: значение уже закоммичено
              // веткой выше, каретке в ячейке оставаться не за чем.
              self.releaseActiveField();
            }
            // self.skipBlur = true;
            // container может быть null если debounce сработал после
            // размонтирования/перерендера — защищаемся optional chaining.
            self.container?.blur();
          }
        }, 200)();
      } else if (event.key === "Backspace") {
        if ((event.ctrlKey || event.metaKey) && self.editable && !field.readonly) {
          event.preventDefault();
          event.stopPropagation();
          if (section.dataset.arrIndex !== undefined) {
            self.eraseSection(section);
          } else {
            self.$emit("clear-field", {
              fieldKey: field.key,
              nestedIndex: section.dataset.arrIndex,
              opts: field.options ? field.options[0] : undefined,
            });
          }
          return;
        }
        const pos = self.getCursorCharPos(section);
        if (pos === 0) {
          self.handleBackspaceAtStart(section);
        }
      } else if (event.key === "ArrowLeft") {
        const pos = self.getCursorCharPos(section);
        if (pos === 0) {
          self.handleArrowLeftAtStart(section);
        } else if (pos > 0 && field.type !== "chip") {
          // self.updateMenu(0);
        }
      } else if (event.key === "ArrowRight") {
        const pos = self.getCursorCharPos(section);
        if (pos === (section.textContent?.length || 0)) {
          self.handleArrowRightAtEnd(section);
        } else if (
          pos < (section.textContent?.length || 0) &&
          field.type !== "chip"
        ) {
          // self.updateMenu(0);
        }
      } else if (event.key === " " && field.type !== "chip") {
        const token = { text: " ", type: "space", clickable: false };
        self.updateToken(token, field);
      }
    };

    /**
     * @brief Handles blur events to save and complete editing.
     *
     * Saves the current section's model, emits done-edit, and hides the options menu when the container
     * loses focus. Skips processing if skipBlur or navigating flags are set to avoid conflicts during
     * section transitions or Enter key handling.
     *
     * @param event FocusEvent The blur event object.
     * @return void
     */
    this.onBlur = (event: FocusEvent, skipBreak = false): void => {
      event.stopPropagation();
      if (self.skipBlur || self.navigating || !self.activeKey) {
        self.skipBlur = false;
        return;
      }

      // Фокус забрало подтверждение действия, открытое из меню. Поле остаётся активным:
      // после отмены пользователь продолжает в нём, а значение коммитится позже.
      const toConfirm = (
        event.relatedTarget as HTMLElement | null
      )?.closest?.(".confirm-popup");
      if (toConfirm) return;

      // Секция, которая потеряла фокус (event.target), а не getCurrentSection(): при клике в другой
      // ParameterInline selection уже переехал, getCurrentSection() вернёт чужую секцию и мы выйдем без закрытия меню.
      const blurredEl = event.target as HTMLElement;
      const section = (blurredEl.closest?.(".section") || (blurredEl.classList?.contains("section") ? blurredEl : null)) as HTMLElement | null;
      if (
        !section ||
        !section.dataset.key ||
        !self.container?.contains(section) ||
        section.dataset.key !== self.activeKey
      ) {
        return;
      }
      const key = section.dataset.key;

      // TODO: separate this into a common function
      let field: SchemaField | undefined = findRecursive(
        function a(element: any, el_to_find: any): any | undefined {
          if (!element) return undefined;
          if (el_to_find === element.key) {
            return element;
          }
          if (element.nestedSchema) {
            return findRecursive(a, el_to_find, element.nestedSchema);
          }
          return undefined;
        },
        key,
        self.schema
      );
      // END TODO

      if (!skipBreak && !field) return;

      self.saveModel(self.activeKey);

      // Не закрываем по blur, если фокус ушёл в меню (в т.ч. на скроллбар fmenu)
      if (!skipBreak && event.relatedTarget) {
        const isMenu = (event.relatedTarget as HTMLElement).closest(".fmenu");
        if (isMenu) {
          return;
        }
      }
      // relatedTarget === undefined: фокус мог уйти на вертикальный скролл селектора (браузер не даёт relatedTarget) — не закрывать меню.
      // Guard снимаем только у полей, которым меню недоступно в принципе. Тип select
      // означает ValueSet, у таких полей меню открывается, поэтому пустой relatedTarget
      // может означать уход фокуса на скроллбар селектора. Признак enableMenu для этой
      // проверки не годится, потому что у вложенных InlineDescriptor он false (меню
      // обязано открываться исключительно у корневого элемента, поэтому openMenu при
      // enableMenu false сразу выходит), а открывает его для них корневой, и guard
      // ошибочно снялся бы с вложенных полей ValueSet.
      const menuPossible = field?.type === "select" || this.plainFieldsOpenMenu;
      if (!skipBreak && event.relatedTarget == null && menuPossible) {
        return;
      }

      this.$emit("update-menu", {
        fieldKey: field?.key ?? null,
        sectionText: section.textContent || "",
        eventKind: 1, // blur с ячейки
        nestedIndex: section.dataset.arrIndex,
        opts: field?.options ? field?.options[0] : undefined,
      });
      self.activeKey = undefined;
    };

    /**
     * @brief Marks the start of IME composition input.
     *
     * Sets the isComposing flag to true to prevent onInput processing during IME input (e.g., for Chinese
     * or Japanese keyboards). Ensures text is processed only after composition ends to avoid partial input.
     *
     * @return void
     */
    this.onCompositionStart = (): void => {
      self.isComposing = true;
    };

    /**
     * @brief Marks the end of IME composition input and processes text.
     *
     * Clears the isComposing flag and triggers onInput to normalize the composed text. Ensures proper
     * handling of IME input (e.g., kanji) after completion, maintaining token structure and cursor position.
     *
     * @return void
     */
    this.onCompositionEnd = (): void => {
      self.isComposing = false;
      self.onInput(new Event("input"));
    };

    /**
     * @brief Handles paste events to insert and normalize pasted text.
     *
     * Intercepts paste events, splits pasted text into tokens (words and spaces), creates corresponding
     * span elements, inserts them into the DOM, and triggers onInput for normalization. Positions the
     * cursor at the end of the pasted content. Handles empty or invalid clipboard data.
     *
     * @param event ClipboardEvent The paste event object.
     * @return void
     */
    this.onPaste = (event: ClipboardEvent): void => {
      event.preventDefault();

      const section = self.getCurrentSection();
      if (!section || section.dataset.key !== self.activeKey) return;
      const field = findRecursive(
        function a(element: any, el_to_find: any): any | undefined {
          if (!element) return undefined;
          if (el_to_find === element.key) {
            return element;
          }
          if (element.nestedSchema) {
            return findRecursive(a, el_to_find, element.nestedSchema);
          }
          return undefined;
        },
        section.dataset.key,
        self.schema
      );

      if (!field) return;
      let text = event.clipboardData?.getData("text") || "";
      if (field.type !== "chip" && field.separator !== null) {
        const sepRegex = new RegExp(
          self.schema.map((f) => f.separator || ",").join("|"),
          "g"
        );
        text = text.replace(sepRegex, " ");
      }
      if (field.inputPattern && field.type !== "chip") {
        text = text.replace(field.inputPattern, "");
      }
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();

        // 1. Получаем чистый текст из события вставки (или из переменной text)
        // Если text — это строка, которую вы получили через event.clipboardData.getData('text')
        let safeText = "";

        if (typeof text === "string") {
          // Хак: создаем временный элемент, чтобы вытащить текст,
          // если в text пришел HTML
          const tmp = document.createElement("DIV");
          tmp.innerHTML = text;
          safeText = tmp.textContent || tmp.innerText || "";
        }

        // Если safeText оказался пустым (например, скопировали только пробельные спаны)
        // или если вы хотите подстраховаться от копирования внутренних структур:
        if (!safeText && text) {
          safeText = text.replace(/<[^>]*>/g, ""); // Грубая очистка регуляркой как запасной вариант
        }

        const tokens = normalizeTokens(
          safeText
        ) /*text.match(/(\s+|[^\s]+)/g)*/ || [safeText];

        const fragment = document.createDocumentFragment();
        let lastCreatedNode = null; // Переменная для хранения ссылки

        tokens.forEach((tok) => {
          const isSpace = /\s+/.test(tok);
          const span = document.createElement("span");
          span.className = isSpace ? "space" : "token";

          const token = {
            text: isSpace ? " " : tok,
            type: isSpace ? "space" : field.type,
            clickable: field.type === "chip",
          };

          const updatedToken = self.updateToken(token, field);
          span.textContent = updatedToken.text;
          span.setAttribute("data-text", updatedToken.text);

          fragment.appendChild(span);
          lastCreatedNode = span; // Запоминаем последний вставленный span
        });

        if (lastCreatedNode) {
          range.insertNode(fragment);
          // Теперь используем сохраненную ссылку
          range.setStartAfter(lastCreatedNode);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      self.onInput(new Event("input"));
    };

    // Реальная реализация закрытия меню по pointerdown вне поля. Через self=this
    // получаем Vue-proxy, на котором живёт accessor self.container (@Ref).
    // Если оставить arrow в class-field initializer, this внутри неё ссылается
    // на raw class-instance (sample), у которого container всегда undefined,
    // и любой клик при открытом меню трактуется как «снаружи».
    this._docPointerDownForClose = (e: PointerEvent): void => {
      const target = e.target as Node;
      const el = e.target as HTMLElement;
      // Подтверждение действия открывается из меню, но телепортировано в body: клик по нему
      // снаружи не считается, иначе отмена возвращала бы пользователя в закрытое поле.
      if (el.closest?.(".confirm-popup")) return;
      let insideMenu = target && el.closest?.(".fmenu");
      const contextMenuRef = self?.$refs?.contextMenu;
      if (!insideMenu && contextMenuRef) {
        const menuEl = contextMenuRef as HTMLElement;
        const r = menuEl.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          insideMenu = menuEl;
        }
      }
      const insideField = self.container?.contains(target);
      if (insideMenu || insideField) return;
      // Всегда закрываем при клике снаружи, даже если showMenu уже false (рассинхрон после скролла и т.п.)
      self.closeMenuFromOutside();
    };

    this.buildContent();

    if (this.editable) {
      this.container.addEventListener("pointerdown", this.onClick);
      this.container.addEventListener("focusin", this.onFocus);
      this.container.addEventListener("input", this.onInput);
      this.container.addEventListener("keydown", this.onKeydown);
      this.container.addEventListener(
        "compositionstart",
        this.onCompositionStart
      );
      this.container.addEventListener("compositionend", this.onCompositionEnd);
      this.container.addEventListener("paste", this.onPaste);

      // if (this.enableMenu) {
      // useEventListener(window, 'scroll', () => { self.throttled() });
      // }
    }
  }

  beforeDestroy(): void {
    // Снимаем document-слушатели меню при любом размонтировании (на случай открытого меню)
    document.removeEventListener("pointerdown", this._docPointerDownForClose, true);
    document.removeEventListener("keydown", this._docKeyDownForClose, true);
    if (this.editable) {
      this.container.removeEventListener("pointerdown", this.onClick);
      this.container.removeEventListener("focusin", this.onFocus);
      this.container.removeEventListener("input", this.onInput);
      this.container.removeEventListener("keydown", this.onKeydown);
      this.container.removeEventListener(
        "compositionstart",
        this.onCompositionStart
      );
      this.container.removeEventListener(
        "compositionend",
        this.onCompositionEnd
      );
      this.container.removeEventListener("paste", this.onPaste);
      this.stopScrollTracking();
    }
    this.nestedRenderers.forEach((renderer) => {
      render(null, renderer); // Очистка вложенных компонентов
    });
    this.nestedRenderers = [];
  }

  get structuredModelClone() {
    return { ...this.modelValue };
  }

  get structuredSchemaClone() {
    return { ...this.schema };
  }

  /**
   * @brief Watches schema changes and updates DOM if no section is active.
   *
   * A Vue watcher for the schema prop. Rebuilds the DOM using buildContent when modelValue
   * changes and no section is being edited (activeKey is undefined). Prevents updates during
   * active editing to avoid overwriting user input. Handles deep changes in schema.
   *
   * @return void
   */
  /** Пока меню открыто — слушатели на document должны быть. Привязка к showMenu, чтобы не терять их после клика по скроллу и т.п. */
  @Watch("showMenu")
  onShowMenuChange(visible: boolean): void {
    this.toggleDocCloseListeners(visible);
  }

  private toggleDocCloseListeners(attach: boolean): void {
    if (attach) {
      this.$nextTick(() => {
        document.removeEventListener("pointerdown", this._docPointerDownForClose, true);
        document.removeEventListener("keydown", this._docKeyDownForClose, true);
        document.addEventListener("pointerdown", this._docPointerDownForClose, true);
        document.addEventListener("keydown", this._docKeyDownForClose, true);
      });
    } else {
      document.removeEventListener("pointerdown", this._docPointerDownForClose, true);
      document.removeEventListener("keydown", this._docKeyDownForClose, true);
    }
  }

  @Watch("structuredSchemaClone", { deep: true })
  onSchemaChange(val: SchemaField[], prev: SchemaField[]): void {
    if (this.navigating) return;
    if (/*!this.activeKey && */ !_.isEqual(val, prev)) {
      this.buildContent();
    }
  }

  /**
   * @brief Watches schema changes and updates DOM if no section is active.
   *
   * A Vue watcher for the schema prop. Rebuilds the DOM using buildContent when modelValue
   * changes and no section is being edited (activeKey is undefined). Prevents updates during
   * active editing to avoid overwriting user input. Handles deep changes in schema.
   *
   * @return void
   */
  @Watch("structuredModelClone", { deep: true })
  onModelValueChange(
    val: Record<string, any>,
    prev: Record<string, any>
  ): void {
    if (this.navigating) return;
    if (/*!this.activeKey && */ !_.isEqual(val, prev)) {
      this.applyModel();
    }
  }

  getNestedModel(key: any) {
    return reactive(this.modelValue[key] || []);
  }

  private setSeparatorOnLastToken(
    section: HTMLElement,
    separator: string
  ): void {
    const lastToken = section.querySelector(
      ".token:last-child, .space:last-child"
    ) as HTMLElement | null;
    if (lastToken) {
      // console.log('set separator on ')
      // console.log(lastToken)
      lastToken.dataset.separator = separator;
    } else {
      // console.log('set separator on ')
      // console.log(section)
      section.dataset.separator = separator;
    }
  }

  placeCaretAtStart(el: HTMLElement, selectAll: boolean = true) {
    if (!el) return;

    el.focus();

    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();

      range.selectNodeContents(el);
      if (!selectAll) {
        range.collapse(true); // true = start
      }

      selection.removeAllRanges();
      selection.addRange(range);
    });
  }

  placeCaretAtEnd(el: HTMLElement, selectAll: boolean = false) {
    if (!el) return;

    el.focus();

    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();

      range.selectNodeContents(el);
      if (!selectAll) {
        range.collapse(false); // false = end
      }

      selection.removeAllRanges();
      selection.addRange(range);
    });
  }

  /**
   * Содержит ли этот уровень ещё один уровень вложенности: запись внутри записи или
   * список. Признак считается здесь, а не селектором, потому что он про ближайший
   * контейнер, а :has() совпал бы со всеми предками сразу, и простая запись внутри
   * сложной получила бы разделители.
   */
  private get hasNestedLevel(): boolean {
    return this.schema.some((f) => f.isList || f.type === "nested");
  }

  /**
   * Класс обёртки секции в табличном режиме. Признака два, и они независимы.
   *
   * --framed это сама коробка: рамка и отступы. Её получают все поля, кроме элементов
   * списка, где рамку рисует вложенное содержимое. Нередактируемые поля коробку тоже
   * получают, иначе их текст по горизонтали не совпадал бы с редактируемыми полями
   * того же столбца в соседних строках.
   *
   * --editable это поведение: смена цвета рамки, плавающая метка, значки и курсор.
   */
  private wrapClass(field: SchemaField, isListItem: boolean = false): string {
    if (isListItem) return "section-native-input";

    return this.editable && !field.readonly
      ? "section-native-input section-native-input--framed section-native-input--editable"
      : "section-native-input section-native-input--framed";
  }

  /**
   * Ключ единицы разметки. Берётся из схемы, потому что устойчивый признак знает тот,
   * кто схему строит. Запасной вариант это ключ поля, он уникален внутри схемы,
   * но позиционный и переезжает при удалении соседей
   */
  private unitKey(field: SchemaField): string {
    return field.unitKey || field.key;
  }

  /**
   * Ключ смонтированного поддерева. Совпадает с ключом единицы намеренно, иначе Vue
   * увидит другой ключ и пересоздаст компонент, и сохранение узла ничего не даст
   */
  private childKey(field: SchemaField): string {
    return `${this.id}-${this.unitKey(field)}`;
  }

  /**
   * Достраивает обёртку с рамкой: плавающая метка и значки справа.
   *
   * Узлы кладутся в обёртку, а не в секцию, по двум причинам. Секцию регулярно вычищает
   * updateSection через innerHTML, и они ушли бы вместе с содержимым. И метке нужно
   * пережить эту очистку, иначе у обратного перехода не будет исходного состояния.
   *
   * Добавляются строго после секции: наличие префикса проверяется по непосредственному
   * предшественнику секции со строгим сравнением класса.
   */
  private appendWrapDecorations(
    wrap: HTMLElement,
    section: HTMLElement,
    field: SchemaField
  ): void {
    const editable = wrap.classList.contains("section-native-input--editable");
    // Формула нередактируема, но метка у неё та же: при значении она стоит на рамке.
    const showLabel =
      this.showFieldLabel && (editable || field.readonly === true);

    if (!editable && !showLabel) return;

    if (showLabel) {
      const label = document.createElement("span");
      label.className = "section-native-input__label";
      label.contentEditable = "false";
      label.textContent = field.placeholder || field.title;
      wrap.appendChild(label);
    }

    if (!editable) return;

    const payload = () => ({
      fieldKey: field.key,
      nestedIndex: section.dataset.arrIndex,
      opts: field.options ? field.options[0] : undefined,
    });

    this.mountClearIcon(wrap, payload);

    this.mountIconHost(wrap, "section-native-input__add", {
      name: "plus",
      width: "12px",
      hoverColor: "#499259",
      tooltip: this.$t("add_value") as string,
      blockWhilePending: true,
      onActivate: () => this.$emit("add-value", payload()),
    });
  }

  private mountClearIcon(
    wrap: HTMLElement,
    payload: () => {
      fieldKey: string;
      nestedIndex: string | undefined;
      opts: any;
    },
    section?: HTMLElement
  ): void {
    this.mountIconHost(wrap, "section-native-input__clear", {
      name: "arrow-left",
      width: "18px",
      hoverColor: "#499259",
      tooltip: this.$t("clear_field_hotkey") as string,
      blockWhilePending: true,
      onActivate: () => {
        // Элемент списка очищается стиранием текста: секция потом читается
        // обратно в модель. Ячейка пишет значение сама, минуя этот прогон.
        if (section) this.eraseSection(section);
        else this.$emit("clear-field", payload());
      },
    });
  }

  private eraseSection(section: HTMLElement): void {
    if (!(section.textContent || "").length) return;
    section.focus();
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(section);
    selection.removeAllRanges();
    selection.addRange(range);
    this.clearCommit = true;
    if (!document.execCommand("delete")) this.clearCommit = false;
  }

  /**
   * Значок рендерится Vue-поддеревом, а не собирается из тегов: подсказка должна быть
   * проектным AppTooltip, а он принимает активатор только слотом.
   *
   * Без onActivate значок остаётся оформлением: обработчики не вешаются, и клик уходит
   * тому, внутри кого значок лежит.
   */
  private mountIconHost(
    wrap: HTMLElement,
    className: string,
    options: {
      name: string;
      width: string;
      hoverColor?: string;
      tooltip?: string;
      cursor?: string;
      blockWhilePending?: boolean;
      onActivate?: (e: MouseEvent) => void;
    }
  ): void {
    const host = document.createElement("span");
    host.className = className;
    host.contentEditable = "false";
    wrap.appendChild(host);

    const icon = () =>
      h(AppIcon, {
        name: options.name,
        width: options.width,
        color: "currentColor",
        hoverColor: options.hoverColor,
        cursor: options.cursor || "pointer",
        // mousedown гасится, иначе клик сначала уводит фокус в секцию и ставит каретку.
        // stopPropagation нужен потому, что делегированный onClick ищет секцию подъёмом
        // по предкам, а значок секции не предок, а сосед, и подъём увёл бы в чужую.
        onMousedown:
          options.onActivate && ((e: MouseEvent) => e.preventDefault()),
        onClick:
          options.onActivate &&
          ((e: MouseEvent) => {
            e.stopPropagation();
            // Пока идёт запрос по значению, значок не отвечает: то же, что у кнопок списка.
            if (
              options.blockWhilePending &&
              host.closest(".inline-descriptor--request-pending")
            ) {
              return;
            }
            options.onActivate?.(e);
          }),
      });

    // Без текста подсказка не навешивается: выдуманный текст хуже его отсутствия.
    const node = options.tooltip
      ? h(AppTooltip, { text: options.tooltip }, { activator: icon })
      : icon();

    // Без appContext внутри поддерева не будет ни i18n, ни плагинов.
    node.appContext = this.$.appContext;
    // Хост значка учитывается сразу при создании, а не при монтировании. Учёт идёт
    // по единицам, и к отложенному тику собираемая единица уже не та
    this.registerIconHost(host);
    this.$nextTick(() => {
      // Единицу могли убрать до этого тика. Монтировать в снятый хост нельзя,
      // размонтировать его потом будет некому
      if (!this.nestedRenderers.includes(host)) {
        return;
      }
      render(node, host);
    });
  }

  private formulaWrap(wrap: HTMLElement, field: SchemaField): HTMLElement {
    if (field.readonly !== true) return wrap;
    const host = this.wrapWithTooltip(wrap, String(this.$t("formula_field_auto")));
    host.classList.add("inline-tip-host--field");
    return host;
  }

  // Кнопка остаётся целью клика: обработчик добавления читает data-атрибуты с неё.
  private wrapWithTooltip(el: HTMLElement, text: string): HTMLElement {
    const host = document.createElement("span");
    host.className = "inline-tip-host";
    host.contentEditable = "false";
    const node = h(
      AppTooltip,
      { text },
      { activator: () => h("span", { class: "inline-tip-host__slot" }) }
    );
    node.appContext = this.$.appContext;
    render(node, host);
    host.querySelector(".inline-tip-host__slot")?.appendChild(el);
    return host;
  }

  private appendListLoader(button: HTMLElement): void {
    const row = button.closest(".inline-title-row") || button.parentElement;
    if (!row) return;
    const dot = document.createElement("span");
    dot.className = "micro-loader";
    row.appendChild(dot);
  }

  private anchorFromEvent(e: MouseEvent): { x: number; y: number } {
    const el = e.currentTarget as HTMLElement | null;
    const rect = el?.getBoundingClientRect?.();
    if (rect) {
      return { x: Math.round(rect.left), y: Math.round(rect.bottom + 2) };
    }
    return { x: e.clientX, y: e.clientY };
  }

  /**
   * Действия над самим параметром, справа от его заголовка и кнопки добавления.
   * Ставятся только на верхнем уровне: у вложенных списков своих действий нет.
   */
  private appendTitleActions(
    titleContainer: HTMLElement,
    title: string
  ): void {
    if (!this.isRootLevel) return;

    const actions = [
      {
        action: "expand",
        name: "arrow-expand",
        hoverColor: "#499259",
        tooltip: this.$t("ui_fullscreen") as string,
      },
      {
        action: "refresh",
        name: "refresh",
        hoverColor: "#499259",
        tooltip: this.$t("param_action_refresh") as string,
      },
      {
        action: "remove",
        name: "delete-outline",
        hoverColor: "#D05A4F",
        tooltip: this.$t("param_action_delete") as string,
      },
    ];

    actions.forEach((item) => {
      // Без цвета наведения значок считается неактивным: он ничего не делает, поэтому
      // ни обработчика, ни отклика на курсор у него нет
      const enabled = item.hoverColor !== undefined;

      this.mountIconHost(
        titleContainer,
        enabled
          ? "inline-title-action"
          : "inline-title-action inline-title-action--disabled",
        {
          name: item.name,
          width: "14px",
          hoverColor: item.hoverColor,
          tooltip: item.tooltip,
          cursor: enabled ? "pointer" : "default",
          onActivate: enabled
            ? (e: MouseEvent) =>
                this.$emit("title-action", {
                  action: item.action,
                  title,
                  anchor: this.anchorFromEvent(e),
                })
            : undefined,
        }
      );
    });
  }

  /**
   * Снимает блокировку кнопок списка и индикацию ожидания.
   *
   * Состояние ставится на время запроса добавления или удаления элемента, а снимается
   * пересборкой разметки. Пересборка не гарантирована: она идёт от изменения схемы,
   * которого при неудачном запросе не будет. Поэтому владелец запроса обязан позвать
   * этот метод, когда запрос закончился любым исходом.
   */
  clearListActionState(): void {
    this.container
      ?.querySelectorAll<HTMLButtonElement>("button[disabled]")
      .forEach((button) => (button.disabled = false));

    this.container
      ?.querySelectorAll(".micro-loader")
      .forEach((loader) => loader.remove());
  }

  @Watch("addValueFieldKey")
  onAddValueFieldKeyChange(key?: string): void {
    this.container
      ?.querySelectorAll(".section-native-input--add-available")
      .forEach((el) =>
        el.classList.remove("section-native-input--add-available")
      );

    if (!key) return;

    this.getSectionByKey(key)
      ?.closest(".section-native-input")
      ?.classList.add("section-native-input--add-available");
  }

  /**
   * @brief Builds the DOM structure for sections and tokens from modelValue.
   *
   * Creates the DOM structure by iterating over schema, generating section and separator spans,
   * and populating tokens (words and spaces) based on modelValue. Ensures proper handling of empty
   * sections for placeholders and numeric fields converted to strings.
   *
   * @return void
   */
  private buildContent(): void {
    this.container.classList.toggle(
      "inline-descriptor--nested",
      this.hasNestedLevel
    );
    this.container.classList.toggle(
      "inline-descriptor--root",
      this.isRootLevel
    );

    const units = KEYED_STRUCTURE_PASS
      ? this.reconcileUnits()
      : this.rebuildAllUnits();

    this.applySeparators(units);

    if (this.isTable) {
      const that = this;
      this.$nextTick(() => {
        that.recalcColumnWidths();
        // Перестройка снесла класс вместе со старыми обёртками, а вотчер сработает
        // только на смену ключа, поэтому состояние восстанавливается здесь.
        that.onAddValueFieldKeyChange(that.addValueFieldKey);
      });
    }
  }

  private get unitTarget(): Node {
    return this.buildTarget || this.container;
  }

  /**
   * Форма единицы. От неё зависит, какие узлы собираются, поэтому при смене формы
   * прежний узел не годится и единица собирается заново даже при том же ключе.
   * Состав списка тоже входит в форму, потому что строки элементов живут внутри единицы,
   * а сверка внутрь единицы не заходит
   */
  private unitShape(field: SchemaField): string {
    const items = (field.nestedSchema || [])
      .map((item) => this.unitKey(item))
      .join(",");

    return [
      field.type || "",
      field.isList ? "list" : "",
      field.component ? "component" : "",
      field.axis || "",
      field.prefix || "",
      items,
    ].join("|");
  }

  /**
   * Признаки секции, которые зависят от поля. Переставленная единица могла переехать
   * на другой ключ поля, он позиционный, а по нему идёт вся адресация секций
   */
  private applyFieldAttrs(
    section: HTMLElement | null,
    field: SchemaField
  ): void {
    if (!section) {
      return;
    }

    section.dataset.key = field.key;
    section.dataset.title = field.placeholder || field.title;

    if (field.readonly || field.isList) {
      section.dataset.forbid_edit = "1";
    } else {
      delete section.dataset.forbid_edit;
    }
  }

  /**
   * Секция, в которой сейчас работает пользователь
   */
  private isLiveSection(section: HTMLElement | null): boolean {
    const active = document.activeElement;
    if (!section || !active) {
      return false;
    }
    return section === active || section.contains(active);
  }

  /**
   * Слепок содержимого секции. Сравнение идёт по значению, а не по ссылке на массив,
   * потому что правка внутри элемента ссылку не меняет, и по ссылке обновление
   * обрывалось бы на уровнях ниже второго
   */
  private contentSignature(value: unknown): string {
    if (!Array.isArray(value)) {
      return "";
    }
    return (value as Token[])
      .map((token) => `${token?.text ?? ""}\u0001${token?.type ?? ""}`)
      .join("\u0000");
  }

  /**
   * Нужно ли пропустить запись в секцию. Живую секцию во время композиции ввода трогать
   * нельзя, иначе набор подерётся с перерисовкой. Неизменившееся не переписывается,
   * иначе каждая смена модели дёргает всё дерево и роняет каретку
   */
  private skipSectionWrite(
    key: string,
    section: HTMLElement,
    value: unknown
  ): boolean {
    if (this.isComposing && this.isLiveSection(section)) {
      return true;
    }

    const signature = this.contentSignature(value);
    if (this.appliedContent.get(key) === signature) {
      return true;
    }

    this.appliedContent.set(key, signature);
    return false;
  }

  /**
   * Устойчивое пустое значение. Новый пустой массив на каждую сборку vnode менял бы
   * идентичность пропа и будил у ребёнка вотчер там, где ничего не произошло
   */
  private emptyModel(key: string): any[] {
    let empty = this.emptyModels.get(key);
    if (!empty) {
      empty = reactive([]);
      this.emptyModels.set(key, empty);
    }
    return empty;
  }

  /**
   * Учёт хоста вложенного компонента. Делается сразу при создании узла, а не при
   * монтировании. Монтирование отложено, и смена модели, пришедшая между сборкой
   * и отложенным тиком, иначе не нашла бы ни хоста, ни сборщика и потерялась бы
   */
  private registerChildHost(key: string, host: HTMLElement): void {
    this.childContainers.set(key, host);
    this.nestedRenderers.push(host);
    this.unitHosts?.push(host);
    this.unitChildKeys?.push(key);
  }

  private registerIconHost(host: HTMLElement): void {
    this.nestedRenderers.push(host);
    this.unitHosts?.push(host);
  }

  /**
   * Отдаёт смонтированному поддереву свежий vnode в тот же узел. Тип и ключ прежние,
   * поэтому Vue обновляет компонент на месте и его состояние сохраняется
   */
  private renderChild(key: string): void {
    const factory = this.childFactories.get(key);
    const host = this.childContainers.get(key);
    if (!factory || !host) {
      return;
    }

    const node = factory();
    render(node, host);
    this.childVNodes.set(key, node);

    const instance = node.component;
    if (instance && instance.proxy) {
      this.childRefs.set(key, instance.proxy);
    }
  }

  private refreshUnitChildren(field: SchemaField): void {
    if (field.component) {
      this.renderChild(this.childKey(field));
    }

    (field.nestedSchema || []).forEach((item) => {
      if (item.component) {
        this.renderChild(this.childKey(item));
      }
    });
  }

  private unitIsConnected(record: UnitRecord): boolean {
    return (
      record.nodes.length > 0 &&
      record.nodes.every((node) => node.parentNode === this.container)
    );
  }

  private dropUnit(record: UnitRecord): void {
    record.hosts.forEach((host) => {
      render(null, host);
      const at = this.nestedRenderers.indexOf(host);
      if (at !== -1) {
        this.nestedRenderers.splice(at, 1);
      }
    });

    record.childKeys.forEach((key) => {
      this.childFactories.delete(key);
      this.childContainers.delete(key);
      this.childVNodes.delete(key);
      this.childRefs.delete(key);
      this.emptyModels.delete(key);
    });

    record.nodes.forEach((node) => node.parentNode?.removeChild(node));
  }

  private createUnit(field: SchemaField): UnitRecord {
    const hosts: HTMLElement[] = [];
    const childKeys: string[] = [];
    const previousHosts = this.unitHosts;
    const previousChildKeys = this.unitChildKeys;

    this.unitHosts = hosts;
    this.unitChildKeys = childKeys;

    let fragment: DocumentFragment;
    try {
      fragment = this.buildFieldUnit(field);
    } finally {
      this.unitHosts = previousHosts;
      this.unitChildKeys = previousChildKeys;
    }

    const nodes = Array.from(fragment.childNodes);
    const section = fragment.querySelector(".section") as HTMLElement | null;
    this.container.appendChild(fragment);

    // Свежая единица нарисована заново, прежние слепки её содержимого ни о чём
    // больше не говорят
    this.appliedContent.delete(field.key);
    (field.nestedSchema || []).forEach((item) =>
      this.appliedContent.delete(item.key)
    );

    return {
      key: this.unitKey(field),
      shape: this.unitShape(field),
      nodes,
      section,
      hosts,
      childKeys,
    };
  }

  /**
   * Расставляет единицы в порядке схемы. Узел трогается только если стоит не на месте,
   * иначе переживший сверку узел пришлось бы переносить без надобности
   */
  private placeUnits(ordered: UnitRecord[]): void {
    let cursor: Node | null = null;

    ordered.forEach((record) => {
      record.nodes.forEach((node) => {
        const expected = cursor ? cursor.nextSibling : this.container.firstChild;
        if (node !== expected) {
          this.container.insertBefore(node, expected);
        }
        cursor = node;
      });
    });
  }

  /**
   * Сверка по ключам. Совпавшая по ключу и форме единица остаётся жить, ей обновляются
   * признаки секции и отдаётся свежий vnode, остальные собираются заново, непарные
   * удаляются вместе со своими поддеревьями
   */
  private reconcileUnits(): UnitRecord[] {
    const previous = this.units;
    const next = new Map<string, UnitRecord>();
    const ordered: UnitRecord[] = [];

    this.schema.forEach((field) => {
      const key = this.unitKey(field);
      const shape = this.unitShape(field);
      const kept = previous.get(key);

      if (kept && kept.shape === shape && this.unitIsConnected(kept)) {
        previous.delete(key);
        this.applyFieldAttrs(kept.section, field);
        this.refreshUnitChildren(field);
        next.set(key, kept);
        ordered.push(kept);
        return;
      }

      if (kept) {
        previous.delete(key);
        this.dropUnit(kept);
      }

      const created = this.createUnit(field);
      next.set(key, created);
      ordered.push(created);
    });

    previous.forEach((record) => this.dropUnit(record));
    this.placeUnits(ordered);
    this.units = next;
    this.closeMenuIfSectionGone();

    return ordered;
  }

  /**
   * Меню держится на секции, которую нашли по активному ключу. Пока секция жива, меню
   * не трогаем. Если её не стало, меню закрывается данными, снятыми при открытии,
   * иначе закрытие ушло бы наверх пустым
   */
  private closeMenuIfSectionGone(): void {
    if (!this.showMenu || !this.activeKey) {
      return;
    }

    if (this.getSectionByKey(this.activeKey)) {
      return;
    }

    const payload = this.cachedClosePayload;
    this.activeKey = undefined;
    this.cachedClosePayload = null;
    this.hideMenu();
    if (payload) {
      this.$emit("update-menu", payload);
    }
  }

  /**
   * Прежний полный проход. Оставлен под переключателем, чтобы сравнивать поведение
   * один в один и быстро откатываться
   */
  private rebuildAllUnits(): UnitRecord[] {
    this.nestedRenderers.forEach((renderer) => render(null, renderer));
    this.nestedRenderers = [];

    this.childRefs = new Map();
    this.childContainers = new Map();
    this.childVNodes = new Map();
    this.childFactories = new Map();
    this.emptyModels = new Map();
    this.appliedContent = new Map();
    this.units = new Map();

    this.container.innerHTML = "";

    const ordered = this.schema.map((field) => this.createUnit(field));
    ordered.forEach((record) => this.units.set(record.key, record));

    return ordered;
  }

  /**
   * Разделители строчного режима. Считаются после того, как порядок единиц установлен.
   * Состояние разделителя позиционное и лежит в двух местах, отдельным узлом между
   * единицами и знаком на последнем токене предыдущей секции, поэтому переносить его
   * вместе с переехавшей единицей нельзя
   */
  private applySeparators(units: UnitRecord[]): void {
    if (this.isTable || !this.container) {
      return;
    }

    // Только свой уровень. Вложенный inline-descriptor рисует свои разделители сам,
    // и поиск по всему поддереву снёс бы их вместе с нашими
    this.container
      .querySelectorAll(":scope > .separator")
      .forEach((node) => node.remove());

    this.schema.forEach((field, index) => {
      if (index === 0 || field.separator === null) {
        return;
      }

      const anchor = units[index]?.nodes[0];
      const prevSection = units[index - 1]?.section;
      if (!anchor || !anchor.parentNode || !prevSection) {
        return;
      }

      this.setSeparatorOnLastToken(
        prevSection,
        field.separator || this.separators[index] || ","
      );

      const sep = document.createElement("span");
      sep.className = "separator";
      sep.contentEditable = "false";
      sep.textContent = "";
      anchor.parentNode.insertBefore(sep, anchor);
    });
  }

  /**
   * Собирает узлы одной единицы схемы во фрагмент. Единица это группа узлов, а не всегда
   * один узел, рядом с секцией могут идти префикс и ячейки таблицы
   */
  private buildFieldUnit(field: SchemaField): DocumentFragment {
    const fragment = document.createDocumentFragment();
    const previousTarget = this.buildTarget;
    this.buildTarget = fragment;
    try {
      this.buildFieldNodes(field);
    } finally {
      this.buildTarget = previousTarget;
    }
    return fragment;
  }

  private buildFieldNodes(field: SchemaField): void {
    const section =
      this.isTable && field.nestedSchema && field.nestedSchema.length
        ? document.createElement("table")
        : document.createElement("span");

    // these are needed at the end when the section is added to a parent <td>
    let addButton: HTMLElement | undefined = undefined;
    let listHasComponent = false;
    let wrapSeection = false;
    let titleText = '';

    // const keyspan = document.createElement('span');
    // keyspan.style.color = 'red';
    // keyspan.textContent = field.key;
    // keyspan.style.display = 'inline-block';
    // section.appendChild(keyspan);

    section.className = "section";
    section.dataset.key = field.key;
    section.dataset.title = field.placeholder || field.title;
    // Formula и прочие readonly-поля: секция нередактируема, ввод/меню по ней не идут
    if (field.readonly) section.dataset.forbid_edit = "1";

    if (this.isTable && field.nestedSchema && field.nestedSchema.length) {
      section.classList.add("parameter-table");
      section.style.display = "block";
    }
    
    if (field.options && field.options[0]?.TemplateId === 999) {
      // TODO: make a css class with the $accent color
      section.style.color = "grey";
      section.style.whiteSpace = "nowrap";
    }
    section.tabIndex = 0;
    if (field.isList) {
      const listModel = reactive(
        Array.isArray(this.modelValue[field.key])
          ? (this.modelValue[field.key] as any)
          : []
      );
      section.dataset.forbid_edit = "1";

      // section.className = 'section-root';
      if (this.editable && this.isTable) {
        addButton = document.createElement("button");
        addButton.dataset.add_detail =
          field.options && field.options[0]
            ? `${field.options[0].TemplateId}_${field.options[0].InstanceId}`
            : undefined;
        addButton.dataset.add = `param_${field.key.split("_")[1]}`;
        // addButton.textContent = "+";
        addButton.className = "inline-button--add";
        // Тот же значок, что и у добавления значения в поле. Обработчик клика читает
        // data-атрибуты прямо с цели события, поэтому целью должна остаться кнопка:
        // значок отключён от указателя стилем.
        this.mountIconHost(addButton, "inline-button__glyph", {
          name: "plus",
          width: "14px",
        });
        // Подсветка на таблице списка: в ней строки элементов. Заголовок и плюс
        // лежат в ячейке рядом с таблицей. Берётся таблица этой ячейки, не вложенная.
        const plusButton = addButton;
        const listTable = () =>
          plusButton.closest("td")?.querySelector(":scope > .parameter-table");
        plusButton.addEventListener("mouseenter", () => {
          listTable()?.classList.add("add-hover-list");
        });
        plusButton.addEventListener("mouseleave", () => {
          listTable()?.classList.remove("add-hover-list");
        });
        addButton = this.wrapWithTooltip(
          addButton,
          this.$t("param_action_add") as string
        );
        // section.appendChild(addButton);
      }

      // console.log('bcont is list');
      // console.log(listModel);
      // console.log(field.nestedSchema);

      if (field.nestedSchema) {
        const listCount = field.nestedSchema.length;
        field.nestedSchema.forEach((nestedField, nestedIndex) => {
          titleText = nestedField.title;
          // TODO: separate this into a commom function
          const div = this.isTable
            ? document.createElement("tr")
            : document.createElement("div");
          if (!this.isTable) {
            div.style.display =
              !this.editable && this.isTable ? "block" : "inline";
            // div.className = 'blocklist';
          }

          // create only if there is more than 1 list item

          if (listCount > 1) {
            const indexSpan = document.createElement(
              this.isTable ? "td" : "span"
            );
            indexSpan.className = "inline-index-cell inline-row-lead";
            // В табличном режиме номер оформлен как обычный текст строки: точка вместо
            // двоеточия и обычное начертание, которое приходит от ячейки
            indexSpan.textContent = this.isTable
              ? `${nestedIndex + 1}. `
              : ` ${nestedIndex + 1}: `;
            // Номер выравнивается по первой строке значения: отступ ячейки значения
            // плюс пиксель на рамку и padding обёртки
            indexSpan.style.paddingTop = "9px";
            if (!this.isTable) indexSpan.style.fontWeight = "500";
            if (this.isTable) {
              // Отсчёт от верха строки, а не от её середины: при выравнивании по центру
              // позиция номера зависит от высоты строки, и при дробной высоте расхождение
              // с полем накапливается от строки к строке
              indexSpan.style.verticalAlign = "top";
            }

            // TODO: make here data-template-id with template id let say 'rownum' or other specific
            // indexSpan.style.width = "50px";
            // indexSpan.style.minWidth = "50px";
            // indexSpan.style.maxWidth = "50px";
            div.appendChild(indexSpan);
          }

          const nestedSection = document.createElement("span");
          nestedSection.className = "section";
          nestedSection.dataset.key = nestedField.key;
          nestedSection.dataset.title =
            nestedField.placeholder || nestedField.title;
          if (nestedField.readonly) nestedSection.dataset.forbid_edit = "1";

          if (this.editable) {
            nestedSection.addEventListener("blur", this.onBlur);
            nestedSection.addEventListener("focus", (event) => {
              // Если фокус уже здесь, не трогаем, пользователь может выделять текст.
              if (document.activeElement === event.target) return;

              event.preventDefault(); // Предотвращаем стандартное действие выделения
              this.placeCaretAtEnd(nestedSection); // Принудительно ставим каретку
            });
          }
          nestedSection.dataset.arrIndex = `${nestedIndex}`;
          nestedSection.tabIndex = 0;

          // const keyspan = document.createElement('span');
          // keyspan.style.color = 'red';
          // keyspan.style.display = 'inline-block';
          // keyspan.textContent = field.key;
          // nestedSection.appendChild(keyspan);
          // END TODO

          if (nestedField.component) {
            listHasComponent = true;
            nestedSection.className = "section-root";
            // У списка из одного элемента строку начинает сам компонент: признак идёт
            // на его обёртку, потому что ячейки создаёт уже вложенный уровень
            if (listCount <= 1) {
              nestedSection.classList.add("inline-row-lead-host");
            }
            if (this.isTable) {
              nestedSection.style.display = "contents"; // 'block';
              section.style.marginTop = "-5px"; // compensate top <td> padding
            }

            const unique_key = this.childKey(nestedField);
            const component = nestedField.component;

            // Список читается заново на каждую сборку vnode. Замыкание на массив,
            // захваченный при сборке разметки, пишет в прежний массив, если модель
            // заменили снаружи, и правка ребёнка уходит в никуда
            const currentList = (): any[] => {
              const raw = this.modelValue[field.key];
              return Array.isArray(raw) ? raw : [];
            };

            this.childFactories.set(unique_key, () => {
              const node = h(component, {
                // Опознание ребёнка идёт от того же устойчивого ключа. От id он строит
                // ключи уже своих детей, и позиционный id ломал бы сохранение узлов
                // на следующем уровне вложенности
                id: unique_key,
                key: unique_key,
                modelValue:
                  currentList()[nestedIndex] || this.emptyModel(unique_key),
                tokenStyles: this.tokenStyles,
                boldSelected: this.boldSelected,
                updateToken: this.updateToken,
                isTable: this.isTable,
                createMode: this.createMode,
                isVertical: nestedField.axis === "vertical",
                editable: this.editable,
                plainFieldsOpenMenu: this.plainFieldsOpenMenu,
                indexPrefix: `${nestedField.key}_`,
                nestedIndex,
                "onUpdate:modelValue": (newValue: any) => {
                  const newModel = {
                    ...this.modelValue,
                    [field.key]: currentList().map((item: any, i: number) =>
                      i === nestedIndex ? newValue : item
                    ),
                  };
                  this.emitUpdateModelValue(newModel);
                },
                "onUpdate:nested": (newValue: any) => {
                  this.$emit("update-menu", newValue);
                },
                // Добавление из поля внутри записи поднимается до корня, к кнопке подсказки.
                onAddValue: () => {
                  this.$emit("add-value");
                },
                onReportWidths: (newWidths: any) => {
                  this.$emit("reportWidths", newWidths);
                },
              });
              node.appContext = this.$.appContext;
              return node;
            });

            this.registerChildHost(unique_key, nestedSection);

            // Монтирование отложено намеренно. К этому моменту узел уже в документе,
            // а вложенному уровню это нужно для замера ячеек. И события от ребёнка
            // наверх приходят после того, как проход закончился, а не посреди него
            this.$nextTick(() => this.renderChild(unique_key));
            div.appendChild(nestedSection);
          } else {
            // const listModel = reactive(Array.isArray(this.modelValue[field.key]) ? this.modelValue[field.key] as any : []);
            const value = listModel[nestedIndex] || [];
            if (this.editable) {
              this.appendTokensToSection(
                nestedSection,
                Array.isArray(value) ? value : [],
                nestedField
              );
              nestedSection.contentEditable =
                nestedSection.dataset.forbid_edit !== "1" &&
                nestedField.type !== "chip"
                  ? "true"
                  : "false";
            } else {
              if (Array.isArray(value)) {
                nestedSection.textContent = (value as Token[])
                  .map((el) => el.text)
                  .join("");
              }
              nestedSection.contentEditable = "false";
            }

            if (this.isTable) {
              const wraptd = document.createElement("td");

              // У списка из одного элемента номера нет, и строку начинает ячейка
              // значения: признак ставится ей, чтобы слева не было отступа
              if (listCount <= 1) wraptd.className = "inline-row-lead";

              nestedSection.style.minWidth = "25px";
              nestedSection.style.display = "inline-block";
              // wrap.className = "section-native-input";
              // Ячейка остаётся ячейкой таблицы: только так она тянется на высоту строки,
              // а от этого зависит фон подсветки, который иначе обрывался бы по её
              // собственной высоте. Верхний отступ у неё общий, из стилей ячеек.
              // Один элемент стоит первой ячейкой строки. Ширина по содержимому,
              // иначе первая ячейка забирает всю свободную ширину таблицы.
              wraptd.style.width = listCount <= 1 ? "auto" : "100%";
              // wrap.style.marginTop = "5px";

              const wrapspan = document.createElement("span");
              wrapspan.className = this.wrapClass(nestedField, true);
              // inline-block обязателен, иначе при переносе значения на несколько строк рамка
              // охватит только последнюю строку, потому что базовая линия inline-блока внутри
              // это базовая линия его последней строки
              wrapspan.style.display = "inline-block";
              wrapspan.appendChild(nestedSection);
              if (this.editable && !nestedField.readonly) {
                wrapspan.classList.add("section-native-input--clear-slot");
                const payload = () => ({
                  fieldKey: nestedField.key,
                  nestedIndex: nestedSection.dataset.arrIndex,
                  opts: nestedField.options
                    ? nestedField.options[0]
                    : undefined,
                });
                this.mountClearIcon(wrapspan, payload, nestedSection);
                // Ветка с компонентом сюда не попадает: у неё свои поля.
                // Плюс тот же, что у рамочной ячейки, и виден по тому же признаку.
                this.mountIconHost(wrapspan, "section-native-input__add", {
                  name: "plus",
                  width: "12px",
                  hoverColor: "#499259",
                  tooltip: this.$t("add_value") as string,
                  blockWhilePending: true,
                  onActivate: () => this.$emit("add-value", payload()),
                });
              }
              wraptd.appendChild(this.formulaWrap(wrapspan, nestedField));

              div.appendChild(wraptd);
            } else {
              div.appendChild(nestedSection);
            }
          }

          if (
            field.nestedSchema &&
            field.nestedSchema.length > 1 &&
            this.editable &&
            this.isTable
          ) {
            const removeButton = document.createElement("button");
            removeButton.dataset.remove_detail =
              nestedField.options && nestedField.options[0]
                ? `${nestedField.options[0].TemplateId}_${nestedField.options[0].InstanceId}_${nestedIndex}`
                : undefined;
            removeButton.dataset.remove = `param_${
              nestedField.key.split("_")[1]
            }_${nestedIndex}`;
            // removeButton.textContent = "-";
            removeButton.className = "inline-button--delete";

            // Подсветка строки ставится классом от события, а не селектором: ближайшая
            // строка этой кнопки прямо выражается через closest, тогда как :has совпал бы
            // разом со всеми строками-предками. Снимать класс при перестройке не нужно,
            // строки не переиспользуются и уходят вместе с содержимым контейнера.
            removeButton.addEventListener("mouseenter", () => {
              removeButton.closest("tr")?.classList.add("delete-hover-row");
            });
            removeButton.addEventListener("mouseleave", () => {
              removeButton
                .closest("tr")
                ?.classList.remove("delete-hover-row");
            });

            if (this.isTable /* && field.nestedSchema[0].component */) {
              const btnCell = document.createElement("td");
              btnCell.className = "inline-delete-cell";
              btnCell.appendChild(removeButton);
              div.appendChild(btnCell);
            } else {
              div.appendChild(removeButton);
            }
          }
          section.appendChild(div);
        });
      }
    } else if (field.type === "nested") {
      section.className = "section-root";
      if (this.isTable) {
        section.style.display = this.isTable ? "inline-flex" : "block";
        section.style.marginTop = field.component ? "-5px" : "0"; // compensate top <td> padding
      }

      if (field.component) {
        const unique_key = this.childKey(field);
        const component = field.component;

        this.childFactories.set(unique_key, () => {
          const node = h(component, {
            id: unique_key,
            key: unique_key,
            modelValue:
              this.modelValue[field.key] || this.emptyModel(unique_key),
            tokenStyles: this.tokenStyles,
            boldSelected: this.boldSelected,
            updateToken: this.updateToken,
            isTable: this.isTable,
            createMode: this.createMode,
            isVertical: field.axis === "vertical",
            editable: this.editable,
            plainFieldsOpenMenu: this.plainFieldsOpenMenu,
            indexPrefix: `${field.key}_`,
            nestedIndex: undefined,
            "onUpdate:modelValue": (newValue: any) => {
              const newModel = { ...this.modelValue, [field.key]: newValue };
              this.emitUpdateModelValue(newModel);
            },
            "onUpdate:nested": (newValue: any) => {
              this.$emit("update-menu", newValue);
            },
            // Добавление из поля внутри записи поднимается до корня, к кнопке подсказки.
            onAddValue: () => {
              this.$emit("add-value");
            },
            onReportWidths: (newWidths: any) => {
              this.$emit("reportWidths", newWidths);
            },
          });
          node.appContext = this.$.appContext;
          return node;
        });

        this.registerChildHost(unique_key, section);
        this.$nextTick(() => this.renderChild(unique_key));
      } else {
        console.warn(`No component defined for nested field ${field.key}`);
      }
    } else {
      section.contentEditable =
        this.editable &&
        section.dataset.forbid_edit !== "1" &&
        this.activeKey === field.key &&
        field.type !== "chip"
          ? "true"
          : "false";
      if (field.prefix) {
        const existingPrefix =
          section.previousSibling?.nodeType === Node.ELEMENT_NODE &&
          (section.previousSibling as HTMLElement).className === "prefix";
        if (!existingPrefix) {
          const prefix = document.createElement("span");
          prefix.className = "prefix";
          prefix.textContent = field.prefix;
          prefix.contentEditable = "false";
          this.unitTarget.appendChild(prefix);
        }
      }
      const value =
        this.modelValue[field.key] ||
        (field.key === "input"
          ? [{ text: "", type: "addtext", clickable: false }]
          : []);
      if (this.editable) {
        const tokens = this.appendTokensToSection(
          section,
          value as Token[],
          field
        );
        if (field.key === "input" && tokens.length === 0) {
          const emptySpan = this.createEmptyToken();

          if (!this.isTable) {
            // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
            // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
            const title = section.dataset.title || "";
            emptySpan.dataset.title = title;
            emptySpan.dataset.separator = this.separators[0] || ',';
          }

          emptySpan.appendChild(document.createTextNode(""));
          section.appendChild(emptySpan);
          section.contentEditable =
            section.dataset.forbid_edit !== "1" && field.type !== "chip"
              ? "true"
              : "false";
          const fieldIndex = this.schema.indexOf(field);
          if (field.separator !== null) {
            emptySpan.dataset.separator =
              field.separator || this.separators[fieldIndex] || ",";
          }
          this.$nextTick(() => {
            const inputSection = this.getSectionByKey("input");
            if (inputSection) {
              const tokenSpan = inputSection.querySelector(
                ".token"
              ) as HTMLElement;
              if (tokenSpan && tokenSpan.isConnected) {
                tokenSpan.focus();
                this.placeCaretAtStart(tokenSpan);
                const range = document.createRange();
                range.setStart(tokenSpan, 0);
                range.collapse(true);
                const sel = window.getSelection();
                if (sel) {
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
                this.updateMenu(2);
              }
            }
          });
        }
        if (field.allowDelete) {
          const deleteSpan = document.createElement("span");
          deleteSpan.className = "delete";
          deleteSpan.textContent = "✕";
          deleteSpan.contentEditable = "false";
          section.appendChild(deleteSpan);
        }
        section.addEventListener("blur", this.onBlur);
        section.addEventListener("focus", (event) => {
          // Если фокус уже здесь, не трогаем, пользователь может выделять текст.
          if (document.activeElement === event.target) return;

          event.preventDefault(); // Предотвращаем стандартное действие выделения
          this.placeCaretAtEnd(section); // Принудительно ставим каретку
        });

        if (this.isTable) {
          wrapSeection = true;
        }
      } else {
        if (Array.isArray(value)) {
          section.textContent = (value as Token[])
            .map((el) => el.text)
            .join("");
        }
        section.contentEditable = "false";
      }
    }

    if (this.isTable) {
      if (this.isVertical) {
        const vcell = document.createElement("tr");
        const cell = document.createElement("td");
        if (field.options && field.options[0]?.TemplateId) {
          if (this.editable) {
            cell.setAttribute(
              "data-template-id",
              field.options[0]?.TemplateId
            );
            cell.style.width = `var(--w-${field.options[0]?.TemplateId})`;
            cell.style.minWidth = `var(--w-${field.options[0]?.TemplateId})`;
            cell.style.maxWidth = `var(--w-${field.options[0]?.TemplateId})`;
          }
        }
        if (addButton) {
          // cell.style.display = "flex";
          // cell.style.alignItems = "flex-start";

          const titleContainer = document.createElement("span");
          titleContainer.className = "inline-title-row";
          
          const title = document.createElement("span");
          title.className = "inline-list-title";
          title.textContent = titleText;
          titleContainer.appendChild(title)

          titleContainer.appendChild(addButton);
          this.appendTitleActions(titleContainer, titleText);

          // cell.appendChild(addButton);
          cell.appendChild(titleContainer);
          section.style.marginTop = listHasComponent ? "-5px" : "0";
        }

        if (wrapSeection) {
          const wrap = document.createElement("span");
          wrap.appendChild(section);

          section.style.minWidth = "25px";
          section.style.display = "inline-block";
          wrap.className = this.wrapClass(field);
          wrap.style.display = "inline-block";
          this.appendWrapDecorations(wrap, section, field);

          cell.appendChild(this.formulaWrap(wrap, field));
        } else {
          cell.appendChild(section);
        }

        vcell.appendChild(cell);
        this.unitTarget.appendChild(vcell);
      } else {
        const cell = document.createElement("td");
        if (field.options && field.options[0]?.TemplateId) {
          if (this.editable) {
            cell.setAttribute(
              "data-template-id",
              field.options[0]?.TemplateId
            );
            cell.style.width = `var(--w-${field.options[0]?.TemplateId})`;
            cell.style.minWidth = `var(--w-${field.options[0]?.TemplateId})`;
            cell.style.maxWidth = `var(--w-${field.options[0]?.TemplateId})`;
          }
        }
        if (addButton) {
          // cell.style.display = "flex";
          // cell.style.alignItems = "flex-start";
          const titleContainer = document.createElement("span");
          titleContainer.className = "inline-title-row";
          
          const title = document.createElement("span");
          title.className = "inline-list-title";
          title.textContent = titleText;
          titleContainer.appendChild(title)

          titleContainer.appendChild(addButton);
          this.appendTitleActions(titleContainer, titleText);

          cell.appendChild(titleContainer);
          section.style.marginTop = listHasComponent ? "-5px" : "0";
        }

        if (wrapSeection) {
          const wrap = document.createElement("span");
          wrap.appendChild(section);

          section.style.minWidth = "25px";
          section.style.display = "inline-block";
          wrap.className = this.wrapClass(field);
          wrap.style.display = "inline-block";
          this.appendWrapDecorations(wrap, section, field);

          cell.appendChild(this.formulaWrap(wrap, field));
        } else {
          cell.appendChild(section);
        }

        this.unitTarget.appendChild(cell);
      }
    } else {
      this.unitTarget.appendChild(section);
    }
  }


  private recalcColumnWidths() {
    if (!this.editable) {
      return;
    }
    // try to calculate widths
    // ensure DOM is ready
    const that = this;
    this.$nextTick(() => {
      if (!that.container) {
        return;
      }
      // Ищем все td внутри компонента
      const tds = that.container.querySelectorAll("td[data-template-id]");
      const widths: { templateId: string; width: number }[] = [];

      tds.forEach((td) => {
        const templateId = td.getAttribute("data-template-id")!;

        const width = td.getBoundingClientRect().width;
        widths.push({ templateId, width });

        // (td as HTMLElement).style.width=`var(--w-${templateId})`;
        // (td as HTMLElement).style.minWidth=`var(--w-${templateId})`;
        // (td as HTMLElement).style.maxWidth=`var(--w-${templateId})`;
      });

      this.$emit("reportWidths", widths);
    });
  }

  /**
   * @brief Builds the tokens from modelValue into the existing DOM structure.
   *
   * Fills up the DOM structure by iterating over schema, generating section and separator spans,
   * and populating tokens (words and spaces) based on modelValue. Ensures proper handling of empty
   * sections for placeholders and numeric fields converted to strings.
   *
   * @return void
   */
  public applyModel(): void {
    this.schema.forEach((field, index) => {
      if (field.isList) {
        const listModel = reactive(
          Array.isArray(this.modelValue[field.key])
            ? (this.modelValue[field.key] as any)
            : []
        );

        if (field.nestedSchema) {
          field.nestedSchema.forEach((nestedField, nestedIndex) => {
            const nestedSection = this.getAnySectionByKey(nestedField.key);

            // reactive dynamic component will watch its model itself
            if (nestedSection && !nestedField.component) {
              const value = listModel[nestedIndex] || [];
              if (this.skipSectionWrite(nestedField.key, nestedSection, value)) {
                return;
              }
              if (this.editable) {
                let charPos = 0;
                if (this.activeKey === nestedField.key) {
                  charPos = this.getCursorCharPos(nestedSection);
                  // console.log("cur pos: " + charPos);
                }

                nestedSection.textContent = "";
                this.appendTokensToSection(
                  nestedSection,
                  Array.isArray(value) ? value : [],
                  nestedField
                );
                nestedSection.contentEditable =
                  nestedSection.dataset.forbid_edit !== "1" &&
                  nestedField.type !== "chip"
                    ? "true"
                    : "false";

                // TODO: make this a separate function
                if (this.activeKey === nestedField.key) {
                  this.$nextTick(() => {
                    // Поле могли отпустить между планированием и этим тиком.
                    if (this.activeKey !== nestedField.key) return;
                    // console.log("set pos: " + charPos);
                    const lastSpan = nestedSection.querySelector(
                      ".token:last-child, .space:last-child"
                    ) as HTMLElement | null;
                    // console.log(lastSpan);
                    if (lastSpan) {
                      this.setCursorCharPos(nestedSection, charPos);
                    } else {
                      const emptySpan = this.createEmptyToken();
                      nestedSection.appendChild(emptySpan);

                      if (!this.isTable) {
                        // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
                        // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
                        const title = nestedSection.dataset.title || "";
                        emptySpan.dataset.title = title;
                        emptySpan.dataset.separator = this.separators[0] || ',';
                      }

                      nestedSection.focus();
                      this.placeCaretAtStart(nestedSection);
                      this.navigating = false;

                      // this.setCursorCharPos(emptySpan, 0);
                    }
                  });
                }
              } else {
                if (Array.isArray(value)) {
                  nestedSection.textContent = (value as Token[])
                    .map((el) => el.text)
                    .join("");
                }
                nestedSection.contentEditable = "false";
              }
            } else if (nestedSection && nestedField.component) {
              // Ребёнок обновляется на месте. Тип и ключ прежние, поэтому Vue правит
              // существующий компонент и его состояние сохраняется, а пропы и
              // обработчики приходят свежие, собранные по текущим данным
              this.renderChild(this.childKey(nestedField));
            }
          });
        }
      } else if (field.type === "nested" && field.component) {
        this.renderChild(this.childKey(field));
      } else {
        const section = this.getSectionByKey(field.key);
        if (!section) {
          return;
        }

        const value =
          this.modelValue[field.key] ||
          (field.key === "input"
            ? [{ text: "", type: "addtext", clickable: false }]
            : []);

        if (this.skipSectionWrite(field.key, section, value)) {
          return;
        }

        let charPos = 0;
        if (this.activeKey === field.key) {
          charPos = this.getCursorCharPos(section);
          // console.log("cur pos: " + charPos);
        }

        // TODO: make this a separate function
        section.contentEditable =
          section.dataset.forbid_edit !== "1" &&
          this.editable &&
          this.activeKey === field.key &&
          field.type !== "chip"
            ? "true"
            : "false";
        if (this.editable) {
          section.textContent = "";
          const tokens = this.appendTokensToSection(
            section,
            value as Token[],
            field
          );

          // TODO: make this a separate function
          if (this.activeKey === field.key) {
            this.$nextTick(() => {
              // Поле могли отпустить между планированием и этим тиком.
              if (this.activeKey !== field.key) return;
              // console.log("set pos: " + charPos);
              const lastSpan = section.querySelector(
                ".token:last-child, .space:last-child"
              ) as HTMLElement | null;
              // console.log(lastSpan);
              if (lastSpan) {
                this.setCursorCharPos(section, charPos);
              } else {
                const emptySpan = this.createEmptyToken();
                section.appendChild(emptySpan);

                if (!this.isTable) {
                  // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
                  // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
                  const title = section.dataset.title || "";
                  emptySpan.dataset.title = title;
                  emptySpan.dataset.separator = this.separators[0] || ',';
                }

                section.focus();
                this.placeCaretAtStart(section);
                this.navigating = false;

                // this.setCursorCharPos(emptySpan, 0);
              }
            });
          }

          if (field.key === "input" && tokens.length === 0) {
            const emptySpan = this.createEmptyToken();
            emptySpan.appendChild(document.createTextNode(""));

            if (!this.isTable) {
              // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
              // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
              const title = section.dataset.title || "";
              emptySpan.dataset.title = title;
              emptySpan.dataset.separator = this.separators[0] || ',';
            }

            section.appendChild(emptySpan);
            section.contentEditable =
              section.dataset.forbid_edit !== "1" && field.type !== "chip"
                ? "true"
                : "false";
            const thisField = this.schema[index];
            if (thisField.separator !== null) {
              emptySpan.dataset.separator =
                thisField.separator || this.separators[index] || ",";
            }
            // TODO: make this a separate function
            this.$nextTick(() => {
              const inputSection = this.getSectionByKey("input");
              if (inputSection) {
                const tokenSpan = inputSection.querySelector(
                  ".token"
                ) as HTMLElement;
                if (tokenSpan && tokenSpan.isConnected) {
                  tokenSpan.focus();
                  this.placeCaretAtStart(tokenSpan);
                  const range = document.createRange();
                  range.setStart(tokenSpan, 0);
                  range.collapse(true);
                  const sel = window.getSelection();
                  if (sel) {
                    sel.removeAllRanges();
                    sel.addRange(range);
                  }
                  this.updateMenu(2);
                }
              }
            });
          }

           if (field && field.separator !== null) {
            this.setSeparatorOnLastToken(
              section,
              field.separator || this.separators[index] || ","
            );
          }
        } else {
          if (Array.isArray(value)) {
            section.textContent = (value as Token[])
              .map((el) => el.text)
              .join("");
          }
          section.contentEditable = "false";
        }
      }
    });

    if (this.isTable) {
      const that = this;
      this.$nextTick(() => {
        that.recalcColumnWidths();
      });
    }
  }

  /**
   * @brief Creates an empty token span for editing in empty sections.
   *
   * Generates a new span element with class "token" for use in empty sections when editing starts.
   * Ensures the section becomes editable without triggering the CSS placeholder. Handles cases where
   * the section is newly activated or clicked.
   *
   * @return HTMLElement The created empty token span.
   */
  private appendTokensToSection(
    section: HTMLElement,
    value: Token[],
    field: SchemaField
  ): Token[] {
    const tokens: Token[] = Array.isArray(value) ? value : [];
    if (tokens.length === 0 && field.type !== "chip") {
      return [];
    }
    tokens.forEach((token) => {
      if (!token.text) return;
      const span = document.createElement("span");
      span.className = token.type === "space" ? "space" : "token";
      if (span.className === "token" && this.tokenStyles) {
        span.style.cssText = this.tokenStyles[field.key] || "";
      }
      span.textContent = span.className === "space" ? " " : token.text;
      span.setAttribute(
        "data-text",
        span.className === "space" ? " " : token.text
      );
      section.appendChild(span);
    });
    return tokens;
  }

  /**
   * @brief Creates an empty token span for editing in empty sections.
   *
   * Generates a new span element with class "token" for use in empty sections when editing starts.
   * Ensures the section becomes editable without triggering the CSS placeholder. Handles cases where
   * the section is newly activated or clicked.
   *
   * @return HTMLElement The created empty token span.
   */
  private createEmptyToken(): HTMLElement {
    const span = document.createElement("span");
    span.className = "token";
    span.textContent = "";
    return span;
  }

  // var section - current section
  private findPreviousValdSection(section: HTMLElement) {
    let prevSection: any = null;

    let curSection = section;
    let prevEL = null;

    const getLastValidSectionInside = (el: HTMLElement): HTMLElement | null => {
      if (el?.classList.contains("section")) {
        return el;
      } else {
        // Ищем ВСЕ подходящие секции внутри найденного соседа
        const allSections = el?.querySelectorAll(
          '.section:not([data-forbid_edit="1"])'
        );
        // И берем ПОСЛЕДНЮЮ из них, так как она ближе всего к текущей позиции
        if (allSections && allSections.length > 0) {
          return allSections[allSections.length - 1] as HTMLElement;
        }
      }
      return null;
    };

    // Функция для поиска предыдущего значимого элемента (не сепаратора)
    const getValidPrevSibling = (
      el: HTMLElement | null
    ): HTMLElement | null => {
      let prev = el?.previousSibling as HTMLElement | null;
      while (
        prev &&
        (prev.nodeType !== 1 ||
          prev.classList.contains("separator") ||
          !["SPAN", "DIV", "TABLE", "TR", "TD"].includes(prev.tagName) ||
          !getLastValidSectionInside(prev))
      ) {
        prev = prev.previousSibling as HTMLElement | null;
      }
      return prev;
    };

    // ОСНОВНОЙ ЦИКЛ
    // 1. Сначала пробуем найти соседа на текущем уровне
    prevEL = getValidPrevSibling(curSection);

    let max_nesting = 6;
    let nesting = 0;
    // 2. Если на текущем уровне соседа нет, начинаем подниматься вверх
    while (!prevEL && curSection.parentElement && nesting < max_nesting) {
      curSection = curSection.parentElement;

      // Ищем соседа у родителя
      prevEL = getValidPrevSibling(curSection);
      nesting++;

      // Если у родителя нашли соседа, содержащего секцию — цикл прервется.
      // Если нет — на следующей итерации поднимемся еще выше.
    }

    if (prevEL) {
      prevSection = getLastValidSectionInside(prevEL);
    }
    return prevSection;
  }

  private findNextValidSection(section: HTMLElement) {
    let nextSection: any = null;
    let curSection = section;
    let nextEL = null;

    const getFirstValidSectionInside = (
      el: HTMLElement
    ): HTMLElement | null => {
      if (el?.classList.contains("section")) {
        return el;
      } else {
        // Ищем ВСЕ подходящие секции внутри найденного соседа
        if (el.classList.contains("section")) {
          return el;
        } else {
          // Иначе ищем первую подходящую секцию внутри соседа (вниз по дереву)
          return el.querySelector('.section:not([data-forbid_edit="1"])');
        }
      }
    };

    // Функция для поиска следующего значимого элемента (не сепаратора)
    const getValidNextSibling = (
      el: HTMLElement | null
    ): HTMLElement | null => {
      // Используем nextSibling для движения вперед
      let next = el?.nextSibling as HTMLElement | null;
      while (
        next &&
        (next.nodeType !== 1 ||
          next.classList.contains("separator") ||
          !["SPAN", "DIV", "TABLE", "TR", "TD"].includes(next.tagName) ||
          !getFirstValidSectionInside(next))
      ) {
        next = next.nextSibling as HTMLElement | null;
      }
      return next;
    };

    // ОСНОВНОЙ ЦИКЛ
    // 1. Сначала пробуем найти соседа справа на текущем уровне
    nextEL = getValidNextSibling(curSection);

    let max_nesting = 6;
    let nesting = 0;
    // 2. Если на текущем уровне соседа нет, поднимаемся вверх к родителю
    while (!nextEL && curSection.parentElement && nesting < max_nesting) {
      curSection = curSection.parentElement;

      // Ищем соседа справа у родителя
      nextEL = getValidNextSibling(curSection);
      nesting++;
    }

    // Проверяем найденный элемент
    if (nextEL) {
      nextSection = getFirstValidSectionInside(nextEL);
    }
    return nextSection;
  }

  private makeSectionActive(
    section: HTMLElement,
    focusLastToken: boolean = true
  ) {
    const lastSpan = section.querySelector(
      focusLastToken
        ? ".token:last-child, .space:last-child"
        : ".token:first-child, .space:first-child"
    ) as HTMLElement | null;
    if (lastSpan) {
      this.setCursorCharPos(
        lastSpan,
        focusLastToken ? lastSpan.textContent?.length || 0 : 0
      );
    } else {
      const emptySpan = this.createEmptyToken();
      section.appendChild(emptySpan);

      if (!this.isTable) {
        // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
        // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
        const title = section.dataset.title || "";
        emptySpan.dataset.title = title;
        emptySpan.dataset.separator = this.separators[0] || ',';
      }

      this.setCursorCharPos(emptySpan, 0);
    }

    if (section.dataset.forbid_edit !== "1") {
      section.contentEditable = "true";
      section.focus();

      if (focusLastToken) {
        this.placeCaretAtEnd(section);
      } else {
        this.placeCaretAtStart(section);
      }
    }
    this.updateMenu(2);
    this.navigating = false;
  }

  /**
   * @brief Handles Backspace at the start of a section for navigation.
   *
   * When Backspace is pressed at the start of a section, saves the current section's model, emits
   * done-edit, and navigates to the previous section by emitting request-edit. Positions the cursor
   * at the end of the previous section. Handles cases with no previous section.
   *
   * @param section HTMLElement The current section element.
   * @return void
   */
  private handleBackspaceAtStart(section: HTMLElement): void {
    const prevSection: any = this.findPreviousValdSection(section);
    if (prevSection && prevSection?.className === "section") {
      const oldKey = section.dataset.key!;
      // TODO: make this a separate function for all schema.findIndex()
      let schemaIndex = this.schema.findIndex((f) => f.key === oldKey);
      // Поиск во вложенных nestedSchema, если не найден в корневом schema
      if (schemaIndex === -1) {
        for (const parentField of this.schema) {
          if (parentField.nestedSchema) {
            schemaIndex = parentField.nestedSchema.findIndex(
              (f) => f.key === oldKey
            );
          }
        }
      }

      if (schemaIndex >= 0) {
        this.navigating = true;
        this.saveModel(oldKey);
        section.contentEditable = "false";
        this.activeKey = prevSection.dataset.key;
        this.$nextTick(() => {
          this.makeSectionActive(prevSection);
        });
      }
    }
  }

  /**
   * @brief Handles ArrowLeft at the start of a section for navigation.
   *
   * When ArrowLeft is pressed at the start of a section, saves the current section's model, emits done-edit,
   * and navigates to the previous section by emitting request-edit. Positions the cursor at the end of the
   * previous section. Handles cases where there is no previous section (first section).
   *
   * @param section HTMLElement The current section element.
   * @return void
   */
  private handleArrowLeftAtStart(section: HTMLElement): void {
    const prevSection: any = this.findPreviousValdSection(section);
    if (prevSection && prevSection?.className === "section") {
      const currentKey = section.dataset.key!;
      // TODO: make this a separate function for all schema.findIndex()
      let schemaIndex = this.schema.findIndex((f) => f.key === currentKey);
      // Поиск во вложенных nestedSchema, если не найден в корневом schema
      if (schemaIndex === -1) {
        for (const parentField of this.schema) {
          if (parentField.nestedSchema) {
            schemaIndex = parentField.nestedSchema.findIndex(
              (f) => f.key === currentKey
            );
          }
        }
      }

      if (schemaIndex >= 0) {
        this.navigating = true;
        this.saveModel(currentKey);
        section.contentEditable = "false";
        this.activeKey = prevSection.dataset.key;
        this.$nextTick(() => {
          /** В данном случае можем случайно сфокусировать секцию, которая находится в container другого родителя
           * (поскольку в nested могут быть иные inline descriptor-ы). Поэтому и нужно событие onFocus, а не только onClick.
           * Чтобы родительский компонент верно настроил текущий activeSection для себя
           * */
          this.makeSectionActive(prevSection);
        });
      }
    }
  }

  /**
   * @brief Handles ArrowRight at the end of a section for navigation.
   *
   * When ArrowRight is pressed at the end of a section, saves the current section's model, emits done-edit,
   * and navigates to the next section by emitting request-edit. Positions the cursor at the start of the
   * next section. Handles cases where there is no next section (last section).
   *
   * @param section HTMLElement The current section element.
   * @return void
   */
  private handleArrowRightAtEnd(section: HTMLElement): void {
    const nextSection = this.findNextValidSection(section);

    if (nextSection && nextSection?.className === "section") {
      const currentKey = section.dataset.key!;
      // TODO: make this a separate function for all schema.findIndex()
      let schemaIndex = this.schema.findIndex((f) => f.key === currentKey);
      // Поиск во вложенных nestedSchema, если не найден в корневом schema
      if (schemaIndex === -1) {
        for (const parentField of this.schema) {
          if (parentField.nestedSchema) {
            schemaIndex = parentField.nestedSchema.findIndex(
              (f) => f.key === currentKey
            );
          }
        }
      }

      if (schemaIndex <= this.schema.length - 1) {
        this.navigating = true;
        this.saveModel(currentKey);
        section.contentEditable = "false";
        this.activeKey = nextSection.dataset.key;
        this.$nextTick(() => {
          /** В данном случае можем случайно сфокусировать секцию, которая находится в container другого родителя
           * (поскольку в nested могут быть иные inline descriptor-ы). Поэтому и нужно событие onFocus, а не только onClick
           * */
          this.makeSectionActive(nextSection, false);
        });
      }
    }
  }

  /**
   * @brief Retrieves a section element by its key.
   *
   * Queries the container for a section element with the specified data-key attribute. Returns null if
   * the section is not found, handling cases where the key is invalid or the DOM is not yet built.
   *
   * @param key string The key of the section to find.
   * @return HTMLElement | null The section element or null if not found.
   */
  private getSectionByKey(key: string): HTMLElement | null {
    return this.container?.querySelector(
      `.section[data-key="${key}"]`
    ) as HTMLElement | null;
  }

  // this one is for searching nested renderers in model value update
  private getAnySectionByKey(key: string): HTMLElement | null {
    return this.container?.querySelector(
      `.section[data-key="${key}"], .section-root[data-key="${key}"]`
    ) as HTMLElement | null;
  }

  /**
   * @brief Gets the current section based on cursor position.
   *
   * Determines the current section by checking the cursor's position in the DOM. Falls back to the active
   * section (via activeKey) if the cursor is outside a section. Handles cases where selection is null or invalid.
   *
   * @return HTMLElement | null The current section element or null if not found.
   */
  private getCurrentSection(): HTMLElement | null {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount)
      return this.activeKey ? this.getSectionByKey(this.activeKey) : null;
    const range = sel.getRangeAt(0);
    let node = range.startContainer;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node = node.parentNode as Node;
    }
    if (!node)
      return this.activeKey ? this.getSectionByKey(this.activeKey) : null;

    // (node as HTMLElement).closest('.section')
    return (
      findClosestWithConditionOuter(
        node as HTMLElement,
        ".section",
        (element: any) => element.dataset.forbid_edit !== "1"
      ) ?? (this.activeKey ? this.getSectionByKey(this.activeKey) : null)
    );
  }

  /**
   * @brief Normalizes a section by splitting text into tokens and spaces.
   *
   * Splits the section's text into tokens (words and spaces) using regex, creates span elements (token or space),
   * applies field type validation (e.g., numbers, select), and repositions the cursor. Ensures empty sections remain
   * empty for non-active sections to show placeholders. Handles cursor position preservation.
   *
   * @param section HTMLElement The section element to normalize.
   * @return void
   */
  // Заменить:
  private normalizeSection(section: HTMLElement): void {
    // TODO: separate this into a common function
    const field = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) {
          return element;
        }
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      section.dataset.key,
      this.schema
    );
    // END TODO

    if (!section || !field) {
      // console.log('normalizeSection: field not found for key', section?.dataset?.key || 'undefined');
      return;
    }
    let text = section.textContent || "";
    // console.log('normalizeSection: text', text);
    const charPos = this.getCursorCharPos(section);
    section.innerHTML = "";
    if (field.prefix && field.type !== "chip" && !this.isTable) {
      const existingPrefix =
        section.previousSibling?.nodeType === Node.ELEMENT_NODE &&
        (section.previousSibling as HTMLElement).className === "prefix";
      if (!existingPrefix) {
        const prefix = document.createElement("span");
        prefix.className = "prefix";
        prefix.textContent = field.prefix;
        prefix.contentEditable = "false";
        section.parentNode?.insertBefore(prefix, section);
      }
    }
    // console.log("normalize section");
    const tokens =
      normalizeTokens(text) /* text.match(/(\s+|[^\s]+)/g) */ || [];
    // console.log('normalizeSection: tokens', tokens);
    let accumulated = 0;
    let targetSpan: HTMLElement | null = null;
    let targetOff = 0;
    if (
      tokens.length === 0 &&
      section.dataset.key !== this.activeKey &&
      field.type !== "chip"
    ) {
      return;
    }
    if (tokens.length === 0) {
      const emptySpan = this.createEmptyToken();
      section.appendChild(emptySpan);

      if (!this.isTable) {
        // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
        // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
        const title = section.dataset.title || "";
        emptySpan.dataset.title = title;
        emptySpan.dataset.separator = this.separators[0] || ',';
      }

      targetSpan = emptySpan;
      targetOff = 0;
    } else {
      tokens.forEach((tok, index) => {
        const isSpace = /\s+/.test(tok);
        const span = document.createElement("span");
        span.className = isSpace ? "space" : "token";
        let tokenText = tok;
        if (!isSpace && field!.inputPattern && field!.type !== "chip") {
          tokenText = tok
            .split("").filter(char => field!.inputPattern?.test(char))
            .join("");
        }
        if (!tokenText) return; // Пропускаем пустые токены
        if (span.className === "token" && this.tokenStyles) {
          span.style.cssText = this.tokenStyles[field!.key] || "";
        }
        const token: Token = {
          text: isSpace ? " " : tokenText,
          type: isSpace ? "space" : field!.type,
          clickable: field!.type === "chip",
        };
        const updatedToken = this.updateToken(token, field!);
        span.textContent = updatedToken.text;
        span.setAttribute("data-text", updatedToken.text);
        section.appendChild(span);
        const len = updatedToken.text.length;
        if (accumulated + len >= charPos) {
          targetSpan = span;
          targetOff = charPos - accumulated;
        }
        accumulated += len;
        // Добавляем data-separator только к последнему непустому токену
        if (index === tokens.length - 1 && field!.separator !== null) {
          span.dataset.separator = field!.separator || ",";
        }
      });
    }
    if (targetSpan) {
      this.setCursorCharPos(targetSpan, targetOff);
    }
    if (field.type !== "chip") {
      // this.updateMenu(2);
    }
  }

  /**
   * @brief Calculates the cursor position within an element's text.
   *
   * Computes the cursor's character position relative to the start of the element's text content, using a range.
   * Limits the position to the element's text length to prevent overflow errors. Returns 0 for invalid selections.
   *
   * @param element HTMLElement The element to measure cursor position in.
   * @return number The cursor position as a character index.
   */
  private getCursorCharPos(element: HTMLElement): number {
    // console.log('get cursor pos: ')
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      // console.log('!sel || !sel.rangeCount')
      // console.log('cursor: 0')
      return 0;
    }

    const range = sel.getRangeAt(0).cloneRange();
    const section =
      findClosestWithConditionOuter(
        element,
        ".section",
        (element: any) => element.dataset.forbid_edit !== "1"
      ) /* element.closest('.section') */ || element;
    // console.log(section)
    // console.log(range)
    const textLength = section.textContent?.length || 0;
    try {
      range.setStart(section, 0);
      const cursorPos = range.toString().length;
      // console.log('cursor d: '+(Number.isFinite(cursorPos) && cursorPos >= 0 ? Math.min(cursorPos, textLength) : 0))
      return Number.isFinite(cursorPos) && cursorPos >= 0
        ? Math.min(cursorPos, textLength)
        : 0;
    } catch {
      // console.log('cursor m: 0')
      return 0;
    }
  }

  /**
   * @brief Sets the cursor to a specific position in an element.
   *
   * Positions the cursor at the specified character index by traversing the element's DOM nodes. Falls back to the
   * end of the element's content if the position is invalid. Handles empty elements and error cases gracefully.
   *
   * @param element HTMLElement The element to set the cursor in.
   * @param pos number The character index to position the cursor at.
   * @return void
   */
  private setCursorCharPos(element: HTMLElement, pos: number): void {
    if (!element.isConnected) return; // Проверяем, что элемент в DOM
    const range = document.createRange();
    const textLength = element.textContent?.length || 0;
    const safePos = Math.min(Math.max(pos, 0), textLength);
    let currentPos = 0;
    const walk = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const len = (node as Text).length;
        const offset = Math.min(safePos - currentPos, len);
        if (currentPos + len >= safePos && offset >= 0) {
          try {
            range.setStart(node, offset);
            range.collapse(true);
            return true;
          } catch {
            return false;
          }
        }
        currentPos += len;
      } else {
        for (const child of node.childNodes) {
          if (walk(child)) return true;
        }
      }
      return false;
    };
    if (!walk(element)) {
      try {
        range.selectNodeContents(element);
        range.collapse(false);
      } catch {
        range.setStart(element, 0);
        range.collapse(true);
      }
    }
    const sel = window.getSelection();
    if (sel) {
      try {
        sel.removeAllRanges();
        sel.addRange(range);
      } catch {
        // Игнорируем ошибки, если selection не удалось установить
      }
    }
  }

  /**
   * @brief Gets the current token element based on cursor position.
   *
   * Retrieves the current span.token element where the cursor is located. Returns null if the cursor is outside a token
   * (e.g., in a space or container). Handles cases where selection is invalid or DOM is unstable.
   *
   * @return HTMLElement | null The current token element or null if not found.
   */
  private getCurrentToken(): HTMLElement | null {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const node = sel.getRangeAt(0).startContainer;
    const parent =
      node.nodeType === Node.TEXT_NODE
        ? node.parentElement
        : (node as HTMLElement);
    if (!parent || typeof parent.closest !== "function") return null;
    return parent.closest(".token") ?? null;
  }

  /**
   * @brief Updates the options menu based on the current token.
   *
   * Shows or hides the options menu for select/select+free fields, filtering options based on the current token's text.
   * Positions the menu relative to the token's bounding box. Hides the menu if no valid token or options exist.
   *
   * @return void
   */

  public updateMenu(eventKind: number = 0): void {
    const field = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) {
          return element;
        }
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      this.getCurrentSection()?.dataset.key,
      this.schema
    );

    if (this.isTable) {
      const that = this;
      this.$nextTick(() => {
        that.recalcColumnWidths();
      });
    }

    // console.log('emit updated model: ')
    // console.log(this.activeKey)
    // console.log(this.getCurrentSection()?.textContent || '')

    const commitClear = eventKind === 0 && this.clearCommit;
    if (commitClear) this.clearCommit = false;
    this.$emit("update-menu", {
      fieldKey: field?.key ?? null,
      sectionText: this.getCurrentSection()?.textContent || "",
      eventKind: eventKind, // 0 - input; 1 - blur; 2 - click;
      nestedIndex: this.getCurrentSection()?.dataset.arrIndex,
      opts: field?.options ? field?.options[0] : undefined,
      commitClear,
    });
  }

  /**
   * Собирает payload для закрытия меню (eventKind 1). Вызывается при mousedown вне меню или keydown Escape/Tab.
   */
  private getCloseMenuPayload(): {
    fieldKey: string | null;
    sectionText: string;
    eventKind: number;
    nestedIndex: string | undefined;
    opts: any;
  } | null {
    if (!this.activeKey) return null;
    // Не используем getCurrentSection(): после клика по скроллбарю selection сбрасывается, и меню перестаёт закрываться по клику снаружи.
    const section = this.getSectionByKey(this.activeKey);
    if (!section || !this.container?.contains(section)) return null;
    const key = section.dataset.key;
    const field: SchemaField | undefined = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) return element;
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      key,
      this.schema
    );
    if (!field) return null;
    return {
      fieldKey: field.key ?? null,
      sectionText: section.textContent || "",
      eventKind: 1,
      nestedIndex: section.dataset.arrIndex,
      opts: field.options ? field.options[0] : undefined,
    };
  }

  /** Закрытие меню по клику вне его или по Escape/Tab. Сначала всегда закрываем меню и снимаем слушатели, потом эмитим payload родителю (если есть). Не выходим по showMenu===false — при рассинхроне после скролла всё равно снимаем слушатели и эмитим. */
  private closeMenuFromOutside(): void {
    if (this.activeKey) this.saveModel(this.activeKey);
    const payload = this.cachedClosePayload || this.getCloseMenuPayload();
    // Escape и клик вне меню в таблице отпускают поле: каретка в нём не остаётся.
    this.releaseActiveField();
    this.activeKey = undefined;
    this.cachedClosePayload = null;
    this.hideMenu();
    if (payload) this.$emit("update-menu", payload);
  }

  // Прямоугольник активного поля в оконных координатах, для того, кто рисует что-то рядом
  // с ячейкой. Секция живёт в том описателе, где стоит поле, у записи это вложенный.
  public activeFieldRect(): DOMRect | null {
    if (this.activeKey) {
      const section = this.getSectionByKey(this.activeKey);
      if (section) return section.getBoundingClientRect();
    }
    let rect: DOMRect | null = null;
    this.childRefs.forEach((child) => {
      if (!rect) rect = child?.fieldRect?.() ?? null;
    });
    return rect;
  }

  // Escape в таблице на наборном поле закрывает его тем же путём, что клик вне поля:
  // значение возвращается из кэша payload'ом eventKind 1. Ключ держит тот описатель, в
  // котором стоит поле, у записи это вложенный, поэтому при пустом ключе спрашиваем детей.
  // Возвращает true, если поле нашлось и закрыто.
  public closeValueSetFieldAsOutside(): boolean {
    if (!this.isTable) return false;
    if (this.activeKey) {
      const field = this.schemaFieldByKey(this.activeKey);
      if (field?.type !== "select") return false;
      this.closeMenuFromOutside();
      return true;
    }
    let handled = false;
    this.childRefs.forEach((child) => {
      if (!handled) handled = !!child?.closeValueSetFieldAsOutside?.();
    });
    return handled;
  }

  private schemaFieldByKey(key: string): SchemaField | undefined {
    return findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) return element;
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      key,
      this.schema
    );
  }

  private _docKeyDownForClose = (e: KeyboardEvent): void => {
    if (!this.showMenu) return;
    if (e.key === "Escape" || e.key === "Tab") {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFromOutside();
    }
  };

  /** Escape/Tab на самом меню (фокус ушёл на меню после клика по скроллу) — document keydown тогда не срабатывает. */
  private onFmenuKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape" || e.key === "Tab") {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFromOutside();
    }
  }

  private hideMenu(): void {
    this.showMenu = false;
    this.cachedClosePayload = null;
    this.toggleDocCloseListeners(false);
    // this.stopScrollTracking();
  }

  // Поле отпускается после выбора, создания значения и закрытия меню, и только в таблице.
  // Каретка живёт в том описателе, где поле: у записи это вложенный, поэтому обход детей.
  // Ключи снимаются по всему поддереву до снятия фокуса. Секция одна, а ключ на неё
  // держит каждый описатель, через который прошёл ввод, и blur при живом ключе ребёнок
  // примет за уход из поля: запишет в модель видимый текст и поднимет его наверх поверх
  // только что выбранного значения.
  public releaseActiveField(): void {
    if (!this.isTable) return;
    const key = this.activeKey;
    this.activeKey = undefined;
    this.childRefs.forEach((child) => {
      child?.releaseFieldFocus?.();
    });
    if (!key) return;
    const section = this.getSectionByKey(key);
    if (!section) return;
    section.contentEditable = "false";
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      (active === section || section.contains(active))
    ) {
      active.blur();
    }
  }

  private startScrollTracking(): void {
    if (this.rafId) return;

    const update = () => {
      if (!this.showMenu) {
        this.stopScrollTracking();
        return;
      }

      // Пересчитываем позицию при скролле
      // const section = this.getCurrentSection();
      // if (section) {
      // const rect = /*section*/this.container.getBoundingClientRect();

      // Пересчитываем только если изменилось
      // const newLeft = rect.left;
      // const newTop = rect.bottom + 8;
      // const newRight = rect.right;
      // const newBottom = rect.top - 8;

      // if ((this.numberLeft && Math.abs(this.numberLeft - newLeft) > 1) ||
      // (this.numberTop && Math.abs(this.numberTop - newTop) > 1) ||
      // (this.numberRight && Math.abs(this.numberRight - newRight) > 1) ||
      // (this.numberBottom && Math.abs(this.numberBottom - newBottom) > 1)) {
      //   this.updateMenu(); // полный пересчёт
      // }
      // }

      this.rafId = requestAnimationFrame(update);
    };

    this.rafId = requestAnimationFrame(update);
  }

  private stopScrollTracking(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * @brief Saves a section's text to modelValue and updates the DOM.
   *
   * Extracts the text from a section, applies field type validation (e.g., number parsing, select option matching), updates
   * modelValue, emits update:modelValue, and refreshes the section's DOM via updateSection. Handles empty or invalid input.
   *
   * @param key string The key of the section to save.
   * @return void
   */
  private saveModel(key: string): void {
    // console.log('save model')
    const section = this.getSectionByKey(key);
    // let field: SchemaField | undefined = this.schema.find((f) => f.key === key);
    // if (!field) {
    // Поиск во вложенных nestedSchema для isList
    // for (const parentField of this.schema) {
    // if (parentField.isList  && parentField.nestedSchema) {
    // field = parentField.nestedSchema.find(f => f.key === key);
    // if (field) break;
    // }
    // }
    // }
    const field = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) {
          return element;
        }
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      key,
      this.schema
    );

    if (!section || !field) return;
    if (field.type === "nested") return; // Модель обновляется вложенным компонентом
    const tokens = Array.from(section.querySelectorAll(".token, .space")).map(
      (span) => ({
        text: (span.className === "space" ? " " : span.textContent) || "",
        type: span.className === "space" ? "space" : field!.type,
        clickable: field!.type === "chip",
      })
    );

    // if spaces only then clear
    if (
      tokens.findIndex((tok) => {
        return !/\s+/.test(tok.text);
      }) < 0
    ) {
      tokens.splice(0);
    }

    const updatedTokens = tokens.map((token) =>
      this.updateToken(token, field!)
    );
    let newModel: Record<
      string,
      Token[] | Record<string, Token[]> | Token[][]
    > = { ...this.modelValue };
    if (section.dataset.arrIndex !== undefined && field) {
      const parentField = this.schema.find(
        (f) => f.isList && f.nestedSchema?.some((nf) => nf.key === key)
      );
      if (parentField && parentField.key) {
        const nestedIndex = parseInt(section.dataset.arrIndex);
        const listModel = Array.isArray(this.modelValue[parentField.key])
          ? (this.modelValue[parentField.key] as Token[][])
          : [];
        newModel = {
          ...this.modelValue,
          [parentField.key]: listModel.map((item, i) =>
            i === nestedIndex ? updatedTokens : item
          ),
        };
        newModel[key] = updatedTokens; // Для updateSection
      }
    } else {
      newModel = { ...this.modelValue, [key]: updatedTokens };
    }
    this.updateSection(key, newModel);
    // Фильтруем ключи вложенных секций перед эмитом
    const filteredModel: Record<
      string,
      Token[] | Record<string, Token[]> | Token[][]
    > = {};
    for (const k in newModel) {
      if (!k.match(/^param_\d+_\d+$/)) {
        // Исключаем ключи вида param_${templateId}_${index}
        filteredModel[k] = newModel[k];
      }
    }
    if (this.editable) {
      // console.log('save model:')
      // console.log(JSON.stringify(this.modelValue))
    }
    this.emitUpdateModelValue(filteredModel);
  }

  /**
   * @brief Updates the DOM for a specific section from modelValue.
   *
   * Rebuilds the DOM for a single section based on its modelValue entry, avoiding full DOM rebuild for performance. Splits
   * the value into tokens and applies tokenStyles. Handles empty sections for placeholders and type-specific formatting.
   *
   * @param key string The key of the section to update.
   * @return void
   */
  private updateSection(
    key: string,
    nm: Record<string, Token[] | Record<string, Token[]> | Token[][]>
  ): void {
    const section = this.getSectionByKey(key);

    // TODO: separate this into a common function
    // let field: SchemaField | undefined = this.schema.find((f) => f.key === key);
    // if (!field) {
    // Поиск во вложенных nestedSchema для isList
    // for (const parentField of this.schema) {
    // if (parentField.isList && parentField.nestedSchema) {
    // field = parentField.nestedSchema.find(f => f.key === key);
    // if (field) break;
    // }
    // }
    // }
    const field = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;
        if (el_to_find === element.key) {
          return element;
        }
        if (element.nestedSchema) {
          return findRecursive(a, el_to_find, element.nestedSchema);
        }
        return undefined;
      },
      key,
      this.schema
    );
    // END TODO

    if (section && field) {
      if (field.type === "nested" || field.isList) return; // Вложенный компонент или список обновляется через v-model
      section.innerHTML = "";
      if (field.type !== "chip") {
        section.dataset.title = field.placeholder || field.title;
      }
      section.contentEditable =
        (section.dataset.forbid_edit !== "1" && this.activeKey === key) ||
        key === "input"
          ? "true"
          : "false";
      const value =
        nm[key] &&
        Array.isArray(nm[key]) &&
        !Array.isArray((nm[key] as any[])[0])
          ? (nm[key] as Token[])
          : key === "input"
          ? [{ text: "", type: "addtext", clickable: false }]
          : [];
      // console.log('updateSection key:', key, 'value:', value);
      const tokens = this.appendTokensToSection(
        section,
        value as Token[],
        field
      );
      if (key === "input" && tokens.length === 0) {
        const emptySpan = this.createEmptyToken();
        section.appendChild(emptySpan);

        if (!this.isTable) {
          // Это не требуется в табличном режиме, так как там поле ввода имеет ширину, плейсхолдер должен пропадать при фокусе
          // Но требуется в линейном режиме, чтобы поле "не схлопывалось"
          const title = section.dataset.title || "";
          emptySpan.dataset.title = title;
          emptySpan.dataset.separator = this.separators[0] || ',';
        }
      }
      // Добавляем data-separator, если секция не последняя
      // TODO: make this a separate function for all schema.findIndex()
      const schemaIndex = this.schema.findIndex((f) => f.key === key);
      // Поиск во вложенных nestedSchema, если не найден в корневом schema
      let thisField = this.schema[schemaIndex];
      if (schemaIndex === -1) {
        for (const parentField of this.schema) {
          if (parentField.nestedSchema) {
            const nestedIndex = parentField.nestedSchema.findIndex(
              (f) => f.key === key
            );
            if (nestedIndex !== -1) {
              thisField = parentField.nestedSchema[nestedIndex];
              break;
            }
          }
        }
      }

      if (thisField && thisField.separator !== null) {
        this.setSeparatorOnLastToken(
          section,
          thisField.separator || this.separators[schemaIndex] || ","
        );
      }
    }
  }

  // Программная активация секции по ключу. Используется внешними
  // компонентами (например, AddressSelectorRefactored), чтобы перевести
  // фокус на следующую секцию после автозамены значения, не дёргая
  // синтетических DOM-событий. Логика повторяет переход по стрелке вправо
  // (см. handleArrowRightAtEnd): сохраняем модель текущей секции, снимаем у
  // неё contentEditable, выставляем activeKey и через nextTick зовём
  // makeSectionActive — он сам поставит caret в начало первого токена,
  // включит редактирование и вызовет updateMenu(2). Поведение существующих
  // путей активации не меняется — это просто обёртка над уже рабочим API.
  public activateSectionByKey(key: string): void {
    if (!key || this.activeKey === key) return;
    const newSection = this.getSectionByKey(key);
    if (!newSection) return;

    this.navigating = true;
    const oldKey = this.activeKey;
    if (oldKey) {
      this.saveModel(oldKey);
      const oldSection = this.getSectionByKey(oldKey);
      if (oldSection) oldSection.contentEditable = "false";
    }
    this.activeKey = key;

    this.$nextTick(() => {
      const liveSection = this.getSectionByKey(key);
      if (liveSection) {
        this.makeSectionActive(liveSection, false);
      } else {
        this.navigating = false;
      }
    });
  }

  // ближайший предок с горизонтальным скроллом - его левый край задаёт видимую границу для меню
  private findHorizontalScrollAncestor(el: HTMLElement | null): HTMLElement | null {
    let node = el?.parentElement ?? null;
    while (node && node !== document.body) {
      const ox = getComputedStyle(node).overflowX;
      if ((ox === "auto" || ox === "scroll") && node.scrollWidth > node.clientWidth) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  // anchorEl — явный якорь вместо секции/контейнера. Нужен, когда меню должно
  // висеть под всем редактором целиком: у инлайновых обёрток
  // getBoundingClientRect() отдаёт объединяющий прямоугольник всех строк,
  // поэтому его bottom — низ последней строки, а не той, где стоит каретка.
  public openMenu(
    positionTarget: "section" | "container" = "container",
    anchorEl?: HTMLElement | null
  ) {
    if (!this.enableMenu) {
      return;
    }

    let targetEl: HTMLElement | null = null;
    let maxw = 100;
    // Инлайновый якорь без единого строчного бокса (пустой селектор) дал бы
    // нулевой rect и увёл меню в угол окна — тогда позиционируем как раньше.
    const anchorRect = anchorEl?.getBoundingClientRect();
    const anchorUsable = !!anchorRect && (anchorRect.width > 0 || anchorRect.height > 0);
    if (anchorEl && anchorUsable) {
      targetEl = anchorEl;
    } else if (positionTarget === "section") {
      targetEl = this.getCurrentSection();
    } else {
      // Берём первого видимого ребёнка
      const container =
        (this.$refs.container as HTMLElement) ?? (this.$el as HTMLElement);
      targetEl = container.children[0] as HTMLElement | null;
      if (!targetEl) targetEl = container;
      maxw =
        container.children[
          container.children.length - 1
        ].getBoundingClientRect().right;
    }

    // Не закрывать меню при !targetEl (напр. рефы не готовы после ре-рендера из saveModel) — иначе при клике по скроллу меню мигает и селекторы перезагружаются
    if (!targetEl) {
      return;
    }

    // reset
    this.numberLeft = 0;
    this.numberTop = 0;
    this.numberRight = 0;
    this.numberBottom = 0;

    const rect = targetEl.getBoundingClientRect();
    // const menuWidth: number = positionTarget === 'container' ? maxw : window.innerWidth/2;
    // const menuHeight: number = window.innerHeight/2.5;

    // === Центрируем по горизонтали ===
    if (rect.left < window.innerWidth / 2) {
      // при горизонтальном скролле строки первая ячейка (якорь в container-режиме) уезжает
      // за левый край и rect.left уходит в минус. Привязываем левый край панели к видимой
      // левой границе ближайшего скролл-контейнера, иначе меню открывается не у строки
      // (или у самого края окна).
      const container =
        (this.$refs.container as HTMLElement) ?? (this.$el as HTMLElement);
      const scrollEl = this.findHorizontalScrollAncestor(container);
      const visibleLeft = scrollEl
        ? Math.max(8, scrollEl.getBoundingClientRect().left)
        : 8;
      this.numberLeft = Math.max(visibleLeft, rect.left);
      this.maxWidth = window.innerWidth - this.numberLeft - 120 + "px";
    } else {
      this.numberRight = window.innerWidth - rect.right;
      this.maxWidth = rect.right - 120 + "px";
    }

    // === Вертикаль: вниз, если хватает места ===
    if (window.innerHeight - rect.bottom > rect.top) {
      this.numberTop = rect.bottom + 8;
      this.maxHeight = window.innerHeight - this.numberTop - 20 + "px";
    } else {
      this.numberBottom = window.innerHeight - rect.top + 8;
      this.maxHeight = rect.top - 20 + "px";
    }

    // === Ограничиваем по экрану ===
    // if (this.numberLeft) {
    //   this.numberLeft = Math.max(10, Math.min(this.numberLeft, window.innerWidth - menuWidth - 10));
    // } else {
    //   this.numberRight = Math.max(10, Math.min(this.numberRight, window.innerWidth - menuWidth - 10));
    // }

    // if (this.numberTop) {
    //   this.numberTop = Math.max(10, Math.min(this.numberTop, window.innerHeight - menuHeight - 10));
    // } else {
    //   this.numberBottom = Math.max(10, Math.min(this.numberBottom, window.innerHeight - menuHeight - 10));
    // }

    this.menuLeft = this.numberLeft > 0 ? this.numberLeft + "px" : "unset";
    this.menuTop = this.numberTop > 0 ? this.numberTop + "px" : "unset";
    this.menuRight = this.numberRight > 0 ? this.numberRight + "px" : "unset";
    this.menuBottom =
      this.numberBottom > 0 ? this.numberBottom + "px" : "unset";

    // this.maxWidth = menuWidth;
    // this.maxHeight = menuHeight;

    this.cachedClosePayload = this.getCloseMenuPayload();
    this.showMenu = true;
    // Слушатели вешаются в @Watch("showMenu") — один источник правды

    // this.startScrollTracking();
  }
}
</script>

<style lang="scss">
.inline-descriptor-wrapper {
  display: contents;
}

.inline-descriptor {
  position: relative;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}

.section {
  display: inline;
  box-sizing: border-box;
  outline: none;
}

.section-root {
  display: inline; /*-block; */
  box-sizing: border-box;
  outline: none;
}

.section[contenteditable="true"] {
  display: inline;
  box-sizing: border-box;
  // background: rgba(0, 0, 0, 0.05);
}

.section:empty:not(
    :has(.token, .space, .inline-descriptor-wrapper, .inline-descriptor)
  ) {
  display: inline-block;
}

/* .section:not(:has(.token:not(:empty), .space))::before */
.section:empty:not(
    :has(.token, .space, .inline-descriptor-wrapper, .inline-descriptor)
  )::before {
  content: attr(data-title);
  display: inline-block;
  color: gray;
}

.section-native-input .section:empty:not(
    :has(.token, .space, .inline-descriptor-wrapper, .inline-descriptor)
  )::before {
  vertical-align: top;
}

/* Каретка в пустом поле должна стоять по глифу, а между перенесёнными строками должен
   оставаться воздух от line-height 1.5. Браузер рисует каретку по верху строки, а глиф
   отстоит от него на половинный интерлиньяж, поэтому интерлиньяж срезается только над
   первой строкой блока, до границы области шрифта, и межстрочные расстояния сохраняются.
   Свойства из CSS Inline Layout 3: Chrome и Edge с 133, Safari с 18.2, Firefox с 154.
   Где поддержки нет, свойства игнорируются и каретка встаёт по верху строки. */
.section-native-input .section {
  text-box-trim: trim-start;
  text-box-edge: text;
}

/* Высоту каретки в пустом поле Chrome берёт от бокса токена, а не от шрифта, поэтому
   у пустого токена снимается inline-block. Признак -webkit-text-security отсекает Firefox,
   где каретка считается по метрикам шрифта и правка не нужна. */
@supports (-webkit-text-security: none) {
  .section-native-input .token:empty {
    display: inline;
  }
}

.token:empty::before {
  content: attr(data-title);
  color: gray;
}

.token:empty:not(:last-child)::after {
  content: attr(data-separator);
  color: black;
}

.section:has(.token:empty):not(
    :has(
        .token:not(:empty),
        .space,
        .inline-descriptor-wrapper,
        .inline-descriptor
      )
  ) {
  display: inline;
}

.section[data-key^="chip_"] {
  cursor: pointer;
}

.prefix,
.delete {
  display: inline;
  pointer-events: auto;
  cursor: pointer;
}

.delete {
  margin-left: 4px;
}

.token {
  white-space: pre-wrap;
  display: inline-block;
  vertical-align: top;
}

.space {
  white-space: pre-wrap;
}

.inline-button {
  // background: transparent;
  border: 0;
  // font-size: 24px;
  padding: 0;
  margin: 2px;
  // margin-top: -10px;
  width: 18px;
  height: 18px;
  color: grey;
  cursor: pointer;
  
  background: no-repeat center / contain;
  /* Плюс берётся тем же файлом и тем же способом, что значок добавления значения в поле,
     то есть встроенным svg. Маской тот же файл подключить нельзя: обводка в нём задана
     через currentColor, а внешний svg под маской его не наследует и даёт пустую альфу.
     Цвет значок берёт от кнопки через currentColor, поэтому наведение и запрет задаются
     здесь, а не внутри значка. */
  &--add {
    @extend .inline-button;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #979797;
  }

  /* Цвет меняется по наведению, поэтому файл идёт маской, а цвет задаётся фоном.
     Файл нужен именно с зашитым цветом обводки: внешний svg, подключённый через mask,
     currentColor от хоста не наследует, и вариант того же крестика на currentColor дал бы
     пустую альфу, то есть невидимый значок. Размер маски явный: бокс 18px оставлен под
     попадание курсора, а сам значок рисуется в 12px. */
  &--delete {
    @extend .inline-button;
    background-color: #D2D2D2;
    mask: url("/src/assets/image/icon-cross.svg") no-repeat center;
    -webkit-mask: url("/src/assets/image/icon-cross.svg") no-repeat center;
    mask-size: 12px 12px;
    -webkit-mask-size: 12px 12px;
  }
}

.inline-button--add:hover {
  color: #499259;
}

/* Значок внутри кнопки не перехватывает указатель: обработчик добавления читает
   data-атрибуты прямо с цели события, и целью должна оставаться кнопка. */
.inline-button__glyph {
  display: inline-flex;
  pointer-events: none;
}

.inline-tip-host,
.inline-tip-host .app-tooltip,
.inline-tip-host .app-tooltip__activator,
.inline-tip-host__slot {
  display: inline-flex;
  line-height: 0;
}

.inline-tip-host--field,
.inline-tip-host--field .app-tooltip,
.inline-tip-host--field .app-tooltip__activator,
.inline-tip-host--field .inline-tip-host__slot {
  line-height: 16px;
}

.inline-button--delete:hover {
  background-color: #D05A4F;
}

/* Заголовок списка перед значком добавления. Селектор идёт через ячейку, потому что
   правило размера для потомков ячейки специфичнее одиночного класса и иначе перебило бы
   этот кегль. */
.inline-descriptor td .inline-list-title {
  margin-right: 5px;
  color: #979797;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
}

/* Тёмный только заголовок того уровня, у которого есть полный набор действий. Селектор
   набирает специфичность двумя классами контейнера, чтобы выигрывать не порядком
   следования, а весом. */
.inline-descriptor.inline-descriptor--root td .inline-list-title {
  color: #4D4D4D;
}

/* Описатели вложенных уровней лежат внутри корневого, поэтому правило выше достаёт и до
   их заголовков, а тёмный нужен ровно одному. Уровень считается вложенностью описателей:
   на каждый уровень приходится ровно один, и лишний предок в селекторе это шаг вглубь. */
.inline-descriptor.inline-descriptor--root .inline-descriptor td .inline-list-title {
  color: #979797;
}

/* Кегль держится ещё на один уровень вглубь и уменьшается со второго вложения. */
.inline-descriptor .inline-descriptor .inline-descriptor td .inline-list-title {
  font-size: 10px;
  line-height: 14px;
}

.inline-title-row {
  display: inline-flex;
  align-items: center;
  margin-bottom: 4px;
}

/* Шаг между значками задают только сами значки, поэтому у кнопки добавления боковые
   отступы из общего класса кнопок здесь снимаются, иначе они складывались бы с ним.
   Бокс такой же, как у соседних значков заголовка: он больше самого знака и держит площадь
   под курсор, а разница между боксом и знаком идёт в строку как прозрачный запас, поэтому
   при разных боксах запас вокруг плюса не совпадал бы с запасом вокруг остальных значков. */
.inline-title-row .inline-button--add {
  margin-left: 0;
  margin-right: 0;
  width: 14px;
  height: 14px;
}

/* Значки подключаются как встроенный svg и рисуются мельче своего поля, поэтому обводка
   ужимается вместе с геометрией: 1.5 в файле с полем 18 даёт на боксе 14px около 1.17px.
   non-scaling-stroke задаёт толщину в пикселях отрисовки, а не в единицах поля, поэтому
   линия остаётся 1.5px при любом боксе. Толщина указана явно, потому что в файлах плюса
   она 2: это вес для поля 18, на 12px он выглядит тяжелее остального ряда. */
.inline-title-action svg path,
.inline-button__glyph svg path,
.section-native-input__clear svg path,
.section-native-input__add svg path {
  vector-effect: non-scaling-stroke;
  stroke-width: 1.5;
}

.inline-title-action {
  display: inline-flex;
  line-height: 0;
  margin-left: 4px;
  color: #979797;
}

.inline-title-action--disabled {
  color: #D2D2D2;
}

/* Ячейка с номером идёт в строке первой, а правило в quasar.scss отдаёт первой ячейке всю
   свободную ширину: оно написано под колонку значения, у которой номера перед ней нет.
   Доля меньше содержимого сажает ячейку на ширину номера, а свободное место достаётся
   колонкам значений. Селектор набирает вес третьим классом, иначе спорил бы по порядку. */
.inline-descriptor .parameter-table td.inline-index-cell {
  width: 1%;
  white-space: nowrap;
}

/* Ячейка, с которой начинается строка списка: номер, а если элемент один, то сразу
   значение. Слева отступа у неё нет, строка начинается по краю таблицы. Признак ставится
   в разметке, а не через :first-child: ячейки вложенного уровня лежат в своей обёртке и
   тоже оказались бы первыми детьми, хотя строку не начинают. */
.inline-descriptor .parameter-table td.inline-row-lead {
  padding-left: 0;
}

/* То же для строки, которую начинает вложенный компонент: ячейки создаёт он сам, поэтому
   класс висит на его обёртке, а отступ снимается с первой ячейки его верхнего уровня.
   Путь выписан дочерними комбинаторами намеренно: он не должен доставать до таблиц внутри
   этой же ячейки, там первые ячейки строку не начинают. Вертикальный уровень отдаёт свои
   строки, поэтому у него первая ячейка лежит в первой строке. */
.inline-row-lead-host > .inline-descriptor-wrapper > .inline-descriptor > td:first-child,
.inline-row-lead-host
  > .inline-descriptor-wrapper
  > .inline-descriptor
  > tr:first-child
  > td:first-child {
  padding-left: 0;
}

/* Раздельная модель границ с нулевым интервалом нужна двум вещам сразу. При схлопнутой
   модели, включённой глобально для всех таблиц, браузеры не рисуют border-radius у ячеек,
   и скругление подсветки пропадает. И тень на строке, которой нарисован разделитель между
   элементами списка, в схлопнутой модели тоже не видна. Интервал нулевой, поэтому зазоров
   между ячейками не появляется. */
.parameter-table {
  border-collapse: separate;
  border-spacing: 0;
}

.add-hover-list {
  --id-surface: #F3F8F4;
  background: #F3F8F4;
  border-radius: 4px;
}

/* Номер строки и промежуток до знака удаления входят в подсветку: фон задан
   на самой строке, не только на её ячейках. */
.delete-hover-row {
  --id-surface: #FBEFEE;
  background: #FBEFEE;
}

/* Фон идёт по потомкам, а не по прямым детям строки: когда в элемент списка вложен
   компонент, его ячейки лежат в обёртке с display: contents. Бокс у такой обёртки
   пропадает, и ячейки становятся ячейками этой строки, но в дереве DOM остаются её
   потомками, поэтому дочерний комбинатор их не видит. */
.delete-hover-row td {
  --id-surface: #FBEFEE;
  background: #FBEFEE;
}

/* Полоса метки красится в цвет строки. Цвет задаётся на обёртке поля и на самой
   полосе: ячейка таблицы это значение метке не отдаёт. Ширина полосы та же,
   красится только прорезь рамки. */
.delete-hover-row .section-native-input {
  --id-surface: #FBEFEE;
}

.delete-hover-row .section-native-input__label {
  background: linear-gradient(#FBEFEE, #FBEFEE) no-repeat center / 100% 6px;
}

/* Скругление берут крайние ячейки строки, включая ячейку со значком удаления: она тоже
   входит в подсветку. Задать радиус на самой строке нельзя: браузеры не рисуют
   border-radius у tr, потому что строка таблицы не образует бокса скругления. */
.delete-hover-row > td:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.delete-hover-row > td:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* На время подсветки разделители сверху и снизу гасятся: они пересекали бы скруглённые
   углы розового блока. Снизу это линия следующей строки, поэтому гасятся две. */
.parameter-table .delete-hover-row,
.parameter-table .delete-hover-row + tr {
  box-shadow: none;
}

.inline-button:disabled {
  color: lightgrey;
}

.section:not(:last-child) .token:last-child::after,
.section:not(:last-child) .space:last-child::after,
.section:not(:last-child):not(:has(.token, .space))::after {
  content: attr(data-separator);
  margin: 0;
  margin-right: 2px;
  display: inline-block;
}

.blocklist::before {
  content: " [";
  display: inline-block;
}

.blocklist::after {
  content: "] ";
  display: inline-block;
}

.separator {
  visibility: hidden;
  display: inline;
  width: 0;
  margin: 0;
  user-select: none;
  -webkit-user-select: none;
}

.span[contenteditable] {
  min-width: 1px;
  min-height: 1em;
}

.fmenu {
  position: fixed;
  background: white;
  border: 1px solid lightgray;
  z-index: 1000;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  min-width: 300px;
  min-height: 200px;
  pointer-events: auto;
  /* overflow задаётся инлайн из openMenu (overflow-x: auto для горизонтального скролла при широком контенте) */
}

.fmenu * {
  pointer-events: auto;
}

.fmenu {
  pointer-events: none;
  /* Фон меню не перехватывает клики */
}

/* Скролл на внутреннем div (pointer-events: auto), чтобы клик по скроллбару не проваливался и всплывал до fmenu */
.fmenu__scroll {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

/** for tables only */
.section-native-input {
  --id-border-color: transparent;
  box-sizing: border-box;
  outline: none;
  padding: 1px 3px !important;
  border-radius: 8px;
  border: 1px solid var(--id-border-color) !important;
  align-items: center;
  vertical-align: top;
  position: relative;
}

/* Типографика табличного режима задаётся на ячейках, а не на обёртке поля: обёртка
   создаётся только для редактируемых полей, и в режиме просмотра секция вместе со своим
   плейсхолдером осталась бы с внешним шрифтом. Ячейки есть в обоих режимах, а в строчном
   режиме их под .inline-descriptor нет вовсе, так что правило туда не попадает. */
.inline-descriptor td {
  position: relative;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: #4D4D4D;
}

/* Отступ над полем задаёт ячейка, поэтому он снимается с общего правила для ячеек таблиц.
   Значение равно врезке вертикального разделителя колонок, и тогда верх рамки поля стоит
   ровно там, где начинается эта линия. Метка в поднятом состоянии сидит на рамке и её
   текст поднимается над ней примерно на 2.75px, то есть остаётся внутри отступа.
   Селектор идёт через таблицу, чтобы выигрывать весом, а не порядком следования. */
.dialog-table .inline-descriptor td,
.parameter-table .inline-descriptor td {
  padding-top: 8px;
}

/* Размер и гарнитура повторяются для всех потомков ячейки, потому что глобальное правило
   в app.scss задаёт их через универсальный селектор каждому элементу поимённо. Наследование
   от ячейки при этом не работает: секция, токены и псевдоэлементы получают глобальные 14px
   напрямую. Универсальный селектор с классом перебивает глобальный по специфичности и
   ограничивает 12px табличным режимом, оставляя 14px строчному. */
.inline-descriptor td,
.inline-descriptor td * {
  font-family: Roboto, "Helvetica Neue", Arial, "Segoe UI", sans-serif;
  font-size: 12px;
}

/* Рамки ячеек принадлежат тому уровню, который владеет колонками, то есть верхнему.
   Правила для ячеек в quasar.scss написаны через потомков и достают до ячеек вложенных
   уровней, а те лежат внутри ячейки своего родителя: их линии шли бы второй парой со
   сдвигом на отступы родителя, и по вертикали, и по горизонтали. Уровень считается
   вложенностью описателей, на каждый уровень приходится ровно один.
   Вес набран двумя классами, чтобы выигрывать у правил для ячеек не порядком, а весом. */
.inline-descriptor .inline-descriptor td {
  border-top-width: 0;
  border-left-width: 0;
}

/* Вертикальный разделитель колонок рисуется псевдоэлементом, а не границей: граница заняла
   бы всю высоту ячейки, а линия должна отступать от горизонтальных на 8px сверху и снизу.
   Линия правая, а не левая, и это принципиально. Вложенный компонент растворяется через
   display: contents, поэтому его ячейки в дереве DOM остаются детьми своей обёртки, а не
   строки. Признак "не последняя" считается внутри своего родителя и поэтому даёт линии
   ровно на стыках колонок на любом уровне, тогда как "не первая" пропустила бы стык
   внешней ячейки с первой ячейкой вложенного компонента.
   Разделители получают только уровни, внутри которых есть ещё один уровень вложенности:
   у линейной записи колонки не разделяются. Дочерний комбинатор здесь обязателен, иначе
   правило сложного уровня дотянулось бы до ячеек простых уровней внутри него.
   До ячейки с номером правило не достаёт по построению: номер лежит в строке списка,
   а не в контейнере уровня. Перед ячейкой со значком удаления линии нет: она отделяла бы
   кнопку, а не колонку. */
.inline-descriptor--nested > td:not(:last-child):not(:has(+ .inline-delete-cell))::after {
  content: "";
  position: absolute;
  top: 8px;
  bottom: 8px;
  right: 0;
  width: 1px;
  background: lightgrey;
}

/* Высота задана явно, а не набрана из строки и отступов, потому что text-box-trim ниже
   срезает верхний половинный интерлиньяж и уменьшает строку примерно на пиксель, а
   поддержан он не везде: Chrome и Edge с 133, Safari с 18.2, Firefox с 154. Без min-height
   поле было бы на пиксель ниже там, где срез работает. Верхний отступ на пиксель больше
   нижнего по той же причине: срез прижимает глифы к верху. */
/* Правый отступ под стрелку только у редактируемого поля: 12 от края до неё, 18 сама
   стрелка, 8 до текста. Он постоянный, потому что ширина поля не должна меняться
   при заполнении. У нередактируемого поля стрелки нет, справа тот же отступ, что слева.
   Под плюс место заранее не держится, он раздвигает поле сам, когда появляется. */
.section-native-input--framed {
  padding: 5px 12px 4px 12px !important;
  min-height: 26px;
  --id-border-color: #F2F2F2;
}

.section-native-input--framed.section-native-input--editable {
  padding-right: 38px !important;
}

/* С плюсом крайним справа становится он, поэтому 12 от края отсчитываются от него,
   а стрелка сдвигается левее на его ширину плюс зазор между значками. */
.section-native-input--framed.section-native-input--editable.section-native-input--add-available {
  padding-right: 56px !important;
}

.section-native-input--add-available .section-native-input__clear {
  right: 30px;
}

/* Заполненность проверяется коротким :has(.section:empty), а не полным признаком
   плейсхолдера с вложенным :not(:has(...)): по спецификации :has() нельзя вкладывать
   в :has(), такой селектор невалиден и правило отбрасывается целиком. Развёрнутая
   проверка тут и не нужна: у :empty-секции дочерних элементов нет по определению. */
.section-native-input--editable:has(.section:empty) {
  --id-border-color: transparent;
}

.section-native-input--editable:not(:has(.section-native-input__label)):has(.section:empty) {
  --id-border-color: #F2F2F2;
}

.section-native-input--editable:has(.section:empty):hover,
.section-native-input--editable:hover,
.section-native-input--editable:not(:has(.section-native-input__label)):has(.section:empty):hover {
  --id-border-color: #3F884F;
}

.section-native-input--editable:focus-within,
.section-native-input--editable:not(:has(.section-native-input__label)):has(.section:empty):focus-within {
  --id-border-color: #3F884F;
}

/* Редактируемое поле отличается от нередактируемого только поведением: рука приглашает
   нажать, а в правке курсор становится текстовым. */
.section-native-input--editable {
  cursor: pointer;
}

.section-native-input--editable:focus-within {
  cursor: text;
}

/* Подчёркивание повторяется на словах, а не только на секции: слова лежат в отдельных
   вставках с блочным поведением, а в такие вставки подчёркивание от родителя не проходит,
   и линия оставалась бы только под пробелами между ними. */
.section-native-input:not(.section-native-input--framed):focus-within .section,
.section-native-input:not(.section-native-input--framed):focus-within .token {
  text-decoration: underline;
  text-decoration-color: #3F884F;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.section-native-input__label {
  position: absolute;
  /* Начало текста метки совпадает с началом текста секции: левый отступ поля минус
     собственный padding метки. При смене отступа это число надо пересчитать. */
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 4px;
  /* Метка закрашивает прорезь в рамке, поэтому под текстом нужна поверхность в цвет фона:
     прозрачной её сделать нельзя, иначе линия рамки пройдёт по тексту подсказки.
     Красится полоса по центру, а не весь бокс: в поднятом состоянии бокс метки поднят
     выше верхнего края ячейки, а закрашенное не должно выходить за ячейку и попадать на
     соседнюю строку. Полосы достаточно, потому что прятать надо только линию рамки, и
     проходит она ровно по центру метки.
     Цвет по умолчанию задан здесь, а не на обёртке: объявление на элементе перекрыло
     бы унаследованное, и поверхность, заданную строкой, метка бы не увидела. */
  background: linear-gradient(
      var(--id-surface, #FFFFFF),
      var(--id-surface, #FFFFFF)
    )
    no-repeat center / 100% 6px;
  color: #979797;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  pointer-events: none;
  white-space: nowrap;
  transition: top 0.3s ease, font-size 0.3s ease, line-height 0.3s ease, color 0.3s ease;
}

.section-native-input--editable:focus-within .section-native-input__label,
.section-native-input--editable:not(:has(.section:empty)) .section-native-input__label,
.section-native-input--framed:not(.section-native-input--editable):not(:has(.section:empty)) .section-native-input__label {
  top: 0;
  font-size: 10px;
  line-height: 14px;
}

.section-native-input--framed:not(.section-native-input--editable):has(.section:empty) .section-native-input__label {
  display: none;
}

.section-native-input--editable:hover:has(.section:empty) .section-native-input__label {
  color: #4D4D4D;
}

/* Плейсхолдер остаётся в потоке невидимой распоркой: ширина ячейки берётся от содержимого,
   и без него пустое поле село бы на min-width. Видимый текст даёт метка поверх него.
   Переносы отключены, чтобы распорка резервировала ровно то место, которое занимает метка:
   метка тоже в одну строку, а с переносами распорка ломалась бы на несколько строк и
   растягивала пустое поле по высоте, оставляя метку в одну строку. */
.section-native-input--editable .section:empty:not(:has(.token, .space, .inline-descriptor-wrapper, .inline-descriptor))::before {
  visibility: hidden;
  white-space: nowrap;
}

.section-native-input--editable:not(:has(.section-native-input__label)) .section:empty:not(:has(.token, .space, .inline-descriptor-wrapper, .inline-descriptor))::before {
  visibility: visible;
}

.section-native-input__clear,
.section-native-input__add {
  position: absolute;
  display: none;
  line-height: 0;
  color: #D2D2D2;
  cursor: pointer;
  pointer-events: auto;
}

/* Значки центрируются по вертикали для случая одной строки: бокс 26px, рамка по 1px,
   отсчёт абсолютного элемента идёт от padding-бокса, отсюда (26 - высота) / 2 - 1.
   При переносе значения на несколько строк значки остаются у верхней строки.
   Плюс опущен ещё на пиксель: его глиф занимает не весь бокс значка, и по расчёту он
   зрительно встаёт выше стрелки очистки. */
.section-native-input__clear {
  right: 12px;
  top: 4px;
}

.section-native-input__add {
  right: 12px;
  top: 7px;
}

.section-native-input--editable:not(:has(.section:empty)):hover .section-native-input__clear {
  display: block;
}

.section-native-input.section-native-input--clear-slot {
  padding-right: 20px !important;
}

.section-native-input.section-native-input--clear-slot .section-native-input__clear {
  top: 0;
  right: 0;
}

.section-native-input.section-native-input--clear-slot:not(:has(.section:empty)):hover .section-native-input__clear {
  display: block;
}

.section-native-input.section-native-input--clear-slot.section-native-input--add-available {
  padding-right: 36px !important;
}

.section-native-input.section-native-input--clear-slot.section-native-input--add-available .section-native-input__clear {
  right: 16px;
}

.section-native-input.section-native-input--clear-slot.section-native-input--add-available .section-native-input__add {
  display: block;
  right: 0;
  top: 3px;
}

.section-native-input.section-native-input--clear-slot .section:not(:last-child) .token:last-child::after,
.section-native-input.section-native-input--clear-slot .section:not(:last-child) .space:last-child::after,
.section-native-input.section-native-input--clear-slot .section:not(:last-child):not(:has(.token, .space))::after {
  content: none;
}

.section-native-input--editable.section-native-input--add-available .section-native-input__add {
  display: block;
}

.inline-descriptor--request-pending .section-native-input__add,
.inline-descriptor--request-pending .section-native-input__clear {
  opacity: 0.5;
  cursor: default;
}

/* За секцией в обёртке стоят метка и значки, поэтому секция здесь не последний ребёнок,
   и правила разделителя, завязанные на :not(:last-child), начали его дорисовывать.
   В табличном режиме разделитель не нужен: каждое поле стоит в своей ячейке. */
.section-native-input--framed .section:not(:last-child) .token:last-child::after,
.section-native-input--framed .section:not(:last-child) .space:last-child::after,
.section-native-input--framed .section:not(:last-child):not(:has(.token, .space))::after {
  content: none;
}

@keyframes micro-pulse {
  0% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.5; }
}

.micro-loader {
  width: 4px;
  height: 4px;
  background-color: #499259;
  border-radius: 50%;
  margin-left: 8px;
  margin-top: -1px;
  animation: micro-pulse 1.2s ease-in-out infinite;
  box-shadow: 0 0 5px rgba(73, 146, 89, 0.4);
}
</style>
